import { supabase } from "../../lib/supabase";
import { EVIDENCE_PROFILE, compressImage as compress } from "../../lib/images";
import type { Contact, Contract, Site } from "../customers/api";

/* ---------------------------------------------------------------- types */

export interface DashboardCounts {
  open_tickets: number;
  breached_tickets: number;
  due_soon_tickets: number;
  critical_open_tickets: number;
  pm_overdue_sites: number;
  pm_due_soon_sites: number;
  contracts_to_chase: number;
  open_faults: number;
}

export type TicketStatus =
  | "new" | "assigned" | "in_progress" | "waiting_client" | "waiting_engineer" | "resolved" | "closed";
export type TicketPriority = "critical" | "high" | "normal" | "low";

export interface TicketRow {
  id: number;
  ticket_number: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  source: string;
  company_id: number | null;
  company_name: string | null;
  site_id: number | null;
  site_name: string | null;
  room_id: number | null;
  room_name: string | null;
  contract_id: number | null;
  cover_level: "gold" | "silver" | "bronze" | null;
  assigned_to: string | null;
  assigned_engineer_name: string | null;
  created_at: string;
  first_response_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  sla_response_deadline: string | null;
  sla_resolution_deadline: string | null;
  sla_paused_minutes: number;
  sla_elapsed_minutes: number;
  sla_response_status: "met" | "no_policy" | "breached" | "due_soon" | "on_track";
  sla_resolution_status: "met" | "no_policy" | "paused" | "breached" | "due_soon" | "on_track";
  message_count: number;
  latest_message_preview: string | null;
  linked_visit_id: number | null;
  linked_visit_date: string | null;
  requester_name: string | null;
  requester_email: string | null;
  requester_phone: string | null;
}

export interface TicketMessage {
  id: number;
  ticket_id: number;
  direction: "inbound" | "outbound" | "internal";
  author_id: string | null;
  author_name: string | null;
  author_email: string | null;
  body: string;
  sent_at: string | null;
  created_at: string;
}

export interface TicketDetail {
  ticket: TicketRow;
  messages: TicketMessage[];
  linked_visits: { id: number; reference: string; visit_date: string | null }[];
}

export interface VisitRow {
  id: number;
  reference: string;
  company_id: number;
  company_name: string;
  site_id: number | null;
  site_name: string | null;
  visit_type: "reactive" | "preventative";
  status: "scheduled" | "in_progress" | "completed" | "cancelled";
  visit_date: string | null;
  engineer_id: string | null;
  engineer_name: string | null;
  room_count: number;
  fault_count: number;
  open_fault_count: number;
  /** Linked rooms on this visit, alphabetical distinct, comma-joined — null
   * when no room is linked (0584). */
  room_names: string | null;
  /** Fault descriptions from rooms flagged faults_found (empties dropped),
   * joined with " · " — null when nothing was faulted (0584). */
  fault_summary: string | null;
  notes: string | null;
}

export interface VisitRoom {
  id: number;
  visit_id: number;
  room_id: number | null;
  room_name?: string | null;
  engineer_id: string | null;
  cable_management: boolean | null;
  table_inputs_cleaned: boolean | null;
  screen_cleaned: boolean | null;
  site_left_clean: boolean | null;
  user_inputs_tested_av: boolean | null;
  audio_tested: boolean | null;
  vc_uc_test_call: boolean | null;
  vc_uc_audio_test: boolean | null;
  freeview_tested: boolean | null;
  lamp_hours_at_visit: string | null;
  filter_clean_service: string | null;
  faults_found: boolean;
  fault_description: string | null;
  date_fault_raised: string | null;
  actions_to_undertake: string | null;
  date_for_completion: string | null;
  fault_repaired: boolean;
  date_of_repair: string | null;
  details_of_repair: string | null;
  notes: string | null;
  photo_file_ids?: number[];
  /** Per-item equipment verification for this visit-room (feature: enforced
   * equipment check). Rides inside the save_visit_rooms payload. */
  equipment_checks?: EquipmentCheck[];
  /** service_equipment_bypass_reasons.code — set when the engineer bypassed
   * the per-item check (empty/incomplete equipment). save_visit_rooms
   * requires this whenever the room's equipment is not fully ticked. */
  equipment_bypass_reason: string | null;
  equipment_bypass_note: string | null;
}

export interface EquipmentCheck {
  equipment_id: number;
  checked: boolean;
  condition?: string | null;
}

/** Equipment checks are keyed by equipment_id. When a room's kit changes —
 * items removed or replaced, leaving archived rows behind — any check whose
 * equipment_id is no longer live must be dropped. Otherwise the client shows
 * those items ticked while save_visit_rooms silently ignores the dead ids (its
 * upsert guard requires a live, in-room equipment row) and then fails the
 * per-room gate, with no sign of which item is at fault. Returns the same
 * array reference when nothing changed, so it is safe to feed straight back
 * into setState without forcing a re-render. */
export function pruneEquipmentChecks(
  checks: EquipmentCheck[] | undefined,
  liveEquipmentIds: Set<number>,
): EquipmentCheck[] | undefined {
  if (!checks || checks.length === 0) return checks;
  const pruned = checks.filter((c) => liveEquipmentIds.has(c.equipment_id));
  return pruned.length === checks.length ? checks : pruned;
}

export interface VisitDetail {
  visit: VisitRow;
  rooms: VisitRoom[];
  linked_tickets: { id: number; ticket_number: string; subject: string; status: TicketStatus }[];
}

export interface RoomAv {
  id: number;
  site_id: number;
  site_name: string;
  company_id: number;
  company_name: string;
  name: string;
  room_type: string | null;
  floor: string | null;
  covered_under_sla: boolean;
  notes: string | null;
  archived_at: string | null;
  has_display: boolean;
  /** Legacy free-text size, e.g. "2 x 65\" Touchscreen" -- authoritative; display_size_inches is only ever a best-effort parse of this. */
  display_size_raw: string | null;
  display_size_inches: number | null;
  display_manufacturer: string | null;
  display_model_no: string | null;
  has_projector: boolean;
  projector_manufacturer: string | null;
  projector_model_no: string | null;
  has_uc_vc: boolean;
  uc_vc_manufacturer: string | null;
  uc_vc_model: string | null;
  equipment_count: number;
  /** storage_path of the room's primary 'room' photo (v_rooms_av picks the
   * is_primary / oldest one), or null when the room has no photo. Rendered as
   * the room-row thumbnail. */
  room_photo_path: string | null;
}

export const EQUIPMENT_CATEGORIES = ["display", "projector", "uc_vc", "audio", "control", "network", "other"] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number];

export interface EquipmentRow {
  id: number;
  site_id: number;
  room_id: number | null;
  manufacturer_id: number | null;
  /** Canonical link to the equipment_models catalogue (which links on to the
   * Product Library). Null = never reconciled — the PM prompt flags these. */
  equipment_model_id: number | null;
  /** Joined from manufacturers -- category alone (audio/uc_vc/...) is a
   * taxonomy, not the brand, and the two were being conflated in the UI. */
  manufacturer_name: string | null;
  /** The linked catalogue model's name (equipment_models), used as the
   * product label when the equipment's own model_number is blank. */
  model_name: string | null;
  category: EquipmentCategory;
  model_number: string | null;
  serial_number: string | null;
  location_in_room: string | null;
  installed_on: string | null;
  notes: string | null;
  /** Outlier flagged for on-site cross-check (0590) — an unidentified item a
   * field engineer should verify on the day. Cleared automatically once the
   * item is linked to a catalogue product. */
  onsite_check_flagged: boolean;
  onsite_check_note: string | null;
}

// --- Enforced equipment check (visit rooms) -----------------------------

export interface BypassReason {
  code: string;
  label: string;
}

export async function fetchEquipmentBypassReasons(): Promise<BypassReason[]> {
  const { data, error } = await supabase
    .from("service_equipment_bypass_reasons")
    .select("code, label")
    .eq("is_active", true)
    .order("sort_order");
  if (error) throw error;
  return (data ?? []) as BypassReason[];
}

// --- Per-room fuzzy product-library match --------------------------------

export interface ProductCandidate {
  profile_id: number;
  confidence: number;
  manufacturer?: string | null;
  model?: string | null;
  label?: string | null;
}

/**
 * Fuzzy-match free text (a manufacturer + a model/description) against the
 * product library, reusing the existing av_resolve_product_candidates RPC
 * (trigram similarity over model/short_description/generic_label/aliases).
 * Returns the top candidates so an engineer can pick the right catalogue
 * item for a room's equipment list. Gated server-side on can_view_service.
 */
export async function searchProductCandidates(
  manufacturer: string | null,
  description: string,
  limit = 5,
): Promise<ProductCandidate[]> {
  const { data, error } = await supabase.rpc("av_resolve_product_candidates", {
    p_manufacturer_text: manufacturer,
    p_description_text: description,
    p_limit: limit,
  });
  if (error) throw new Error(error.message);
  // The RPC returns the identity block from app.av_matched_identity(), whose
  // keys are prefixed matched_* (matched_manufacturer/matched_model/
  // matched_description) -- reading the bare manufacturer/model/label names
  // here left every candidate with null identity, so the match list rendered
  // each row as a meaningless "#<profile_id>" and "Add" then created blank,
  // unnamed equipment (addProductToRoomEquipment reads these same fields).
  // Same keys the quotes Builder already reads off this RPC family.
  return ((data ?? []) as Record<string, unknown>[]).map((c) => ({
    profile_id: Number(c.profile_id),
    confidence: Number(c.confidence),
    manufacturer: (c.matched_manufacturer as string | null) ?? null,
    model: (c.matched_model as string | null) ?? null,
    label: (c.matched_description as string | null) ?? null,
  }));
}

export interface ManufacturerOption {
  id: number;
  name: string;
}

export async function fetchManufacturerOptions(): Promise<ManufacturerOption[]> {
  const { data, error } = await supabase.from("manufacturers").select("id, name").order("name");
  if (error) throw error;
  return data ?? [];
}

export interface ContractOption {
  id: number;
  reference: string | null;
  cover_level: "gold" | "silver" | "bronze";
}

export interface EngineerOption {
  id: string;
  full_name: string | null;
}

/* --------------------------------------------------------------- reads */

export async function fetchDashboard(): Promise<DashboardCounts> {
  const [tickets, pm, chase, faults] = await Promise.all([
    supabase.from("v_tickets_with_sla").select("status, priority, sla_response_status, sla_resolution_status"),
    supabase.from("v_pm_detail").select("pm_visit_status"),
    supabase.from("v_contracts_to_chase").select("id", { count: "exact", head: true }),
    supabase.from("v_onsite_issues").select("id", { count: "exact", head: true }),
  ]);
  if (tickets.error) throw tickets.error;
  if (pm.error) throw pm.error;
  if (chase.error) throw chase.error;
  if (faults.error) throw faults.error;

  const open = (tickets.data ?? []).filter((t) => t.status !== "resolved" && t.status !== "closed");
  return {
    open_tickets: open.length,
    breached_tickets: open.filter(
      (t) => t.sla_response_status === "breached" || t.sla_resolution_status === "breached",
    ).length,
    due_soon_tickets: open.filter(
      (t) => t.sla_response_status === "due_soon" || t.sla_resolution_status === "due_soon",
    ).length,
    critical_open_tickets: open.filter((t) => t.priority === "critical").length,
    pm_overdue_sites: (pm.data ?? []).filter((s) => s.pm_visit_status === "overdue").length,
    pm_due_soon_sites: (pm.data ?? []).filter((s) => s.pm_visit_status === "due_soon").length,
    contracts_to_chase: chase.count ?? 0,
    open_faults: faults.count ?? 0,
  };
}

export async function fetchTickets(filter?: {
  status?: string;
  site_id?: number;
  company_id?: number;
  room_id?: number;
}): Promise<TicketRow[]> {
  let q = supabase.from("v_tickets_with_sla").select("*").order("created_at", { ascending: false });
  if (filter?.status && filter.status !== "all") q = q.eq("status", filter.status);
  if (filter?.site_id) q = q.eq("site_id", filter.site_id);
  if (filter?.company_id) q = q.eq("company_id", filter.company_id);
  if (filter?.room_id) q = q.eq("room_id", filter.room_id);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as TicketRow[];
}

export async function fetchTicketDetail(ticketId: number): Promise<TicketDetail> {
  const [ticketRes, messagesRes, linksRes] = await Promise.all([
    supabase.from("v_tickets_with_sla").select("*").eq("id", ticketId).maybeSingle(),
    supabase.from("ticket_messages").select("*").eq("ticket_id", ticketId).order("created_at"),
    supabase
      .from("ticket_visit_links")
      .select("service_visits(id, reference, visit_date)")
      .eq("ticket_id", ticketId),
  ]);
  if (ticketRes.error) throw ticketRes.error;
  if (messagesRes.error) throw messagesRes.error;
  if (linksRes.error) throw linksRes.error;

  return {
    ticket: ticketRes.data as TicketRow,
    messages: (messagesRes.data ?? []) as TicketMessage[],
    linked_visits: ((linksRes.data ?? []) as unknown as { service_visits: { id: number; reference: string; visit_date: string | null } }[])
      .map((r) => r.service_visits)
      .filter(Boolean),
  };
}

export async function fetchVisits(filter?: {
  visit_type?: "reactive" | "preventative";
  company_id?: number;
}): Promise<VisitRow[]> {
  let q = supabase.from("v_service_visits_list").select("*").order("visit_date", { ascending: false, nullsFirst: false });
  if (filter?.visit_type) q = q.eq("visit_type", filter.visit_type);
  if (filter?.company_id) q = q.eq("company_id", filter.company_id);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as VisitRow[];
}

export interface VisitsPage {
  rows: VisitRow[];
  total: number;
}

/**
 * Server-side page: the `.range()` + `count: "exact"` combination is one
 * PostgREST round-trip that returns only the requested page, not the whole
 * table sliced client-side — the count comes from the same query via a
 * window function, not a second COUNT(*) request. `page` is 1-based.
 */
export async function fetchVisitsPage(opts: {
  page: number;
  pageSize: number;
  visit_type?: "reactive" | "preventative";
  search?: string;
  site_id?: number;
  has_open_faults?: boolean;
}): Promise<VisitsPage> {
  const { page, pageSize, visit_type, search, site_id, has_open_faults } = opts;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase
    .from("v_service_visits_list")
    .select("*", { count: "exact" })
    .order("visit_date", { ascending: false, nullsFirst: false })
    .range(from, to);

  if (visit_type) q = q.eq("visit_type", visit_type);
  if (site_id) q = q.eq("site_id", site_id);
  if (has_open_faults) q = q.gt("open_fault_count", 0);

  // Strip characters that are syntactically meaningful to PostgREST's
  // .or() filter grammar so a search term like "Acme, Ltd" can't be
  // misread as two separate filter conditions.
  const needle = search?.trim().replace(/[(),]/g, "");
  if (needle) {
    q = q.or(
      `reference.ilike.%${needle}%,company_name.ilike.%${needle}%,site_name.ilike.%${needle}%,engineer_name.ilike.%${needle}%`,
    );
  }

  const { data, error, count } = await q;
  if (error) throw error;
  return { rows: (data ?? []) as VisitRow[], total: count ?? 0 };
}

export async function fetchVisitDetail(visitId: number): Promise<VisitDetail> {
  const [visitRes, roomsRes, ticketLinksRes] = await Promise.all([
    supabase.from("v_service_visits_list").select("*").eq("id", visitId).maybeSingle(),
    supabase.from("service_visit_rooms").select("*, rooms(name)").eq("visit_id", visitId).order("id"),
    supabase
      .from("ticket_visit_links")
      .select("tickets(id, ticket_number, subject, status)")
      .eq("visit_id", visitId),
  ]);
  if (visitRes.error) throw visitRes.error;
  if (roomsRes.error) throw roomsRes.error;
  if (ticketLinksRes.error) throw ticketLinksRes.error;

  const rawRooms = (roomsRes.data ?? []) as unknown as Array<VisitRoom & { rooms: { name: string } | null }>;

  // Existing per-item equipment checks for the saved rooms on this visit, so
  // the editor can pre-tick what was already verified (and re-sending them on
  // save keeps a previously-satisfied room's equipment gate satisfied).
  const savedRoomIds = rawRooms.map((r) => r.id).filter((id) => id > 0);
  let checksByRoom: Record<number, EquipmentCheck[]> = {};
  if (savedRoomIds.length > 0) {
    const checksRes = await supabase
      .from("service_visit_room_equipment")
      .select("service_visit_room_id, equipment_id, checked, condition")
      .in("service_visit_room_id", savedRoomIds);
    if (checksRes.error) throw checksRes.error;
    checksByRoom = (checksRes.data ?? []).reduce<Record<number, EquipmentCheck[]>>((acc, c) => {
      (acc[c.service_visit_room_id] ??= []).push({
        equipment_id: c.equipment_id,
        checked: c.checked,
        condition: c.condition,
      });
      return acc;
    }, {});
  }

  const rooms = rawRooms.map((r) => ({
    ...r,
    room_name: r.rooms?.name ?? null,
    equipment_checks: checksByRoom[r.id] ?? [],
  }));

  const linked_tickets = (
    (ticketLinksRes.data ?? []) as unknown as {
      tickets: { id: number; ticket_number: string; subject: string; status: TicketStatus } | null;
    }[]
  )
    .map((r) => r.tickets)
    .filter((t): t is { id: number; ticket_number: string; subject: string; status: TicketStatus } => t != null);

  return { visit: visitRes.data as VisitRow, rooms, linked_tickets };
}

export async function fetchRoomsForSite(siteId: number): Promise<{ id: number; name: string }[]> {
  const { data, error } = await supabase
    .from("rooms")
    .select("id, name")
    .eq("site_id", siteId)
    .is("archived_at", null)
    .order("name");
  if (error) throw error;
  return data ?? [];
}

export async function fetchRoomsAv(companyId: number): Promise<RoomAv[]> {
  const { data, error } = await supabase.from("v_rooms_av").select("*").eq("company_id", companyId).order("name");
  if (error) throw error;
  return (data ?? []) as RoomAv[];
}

export async function fetchEquipment(siteId: number, opts?: { room_id?: number }): Promise<EquipmentRow[]> {
  let q = supabase
    .from("equipment")
    .select("*, manufacturers(name), equipment_models(model_number, description)")
    .eq("site_id", siteId)
    .is("archived_at", null)
    .order("category");
  if (opts?.room_id) q = q.eq("room_id", opts.room_id);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as unknown as Array<EquipmentRow & { manufacturers: { name: string } | null; equipment_models: { model_number: string | null; description: string | null } | null }>).map((r) => {
    const { manufacturers, equipment_models, ...rest } = r;
    return {
      ...rest,
      manufacturer_name: manufacturers?.name ?? null,
      model_name: equipment_models?.model_number ?? equipment_models?.description ?? null,
    };
  });
}

export interface RoomInput {
  site_id: number;
  name: string;
  room_type: string | null;
  floor: string | null;
  covered_under_sla: boolean;
  has_display: boolean;
  has_projector: boolean;
  has_uc_vc: boolean;
  notes: string | null;
}

export async function createRoom(input: RoomInput): Promise<number> {
  const { data, error } = await supabase.from("rooms").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function updateRoom(roomId: number, input: RoomInput): Promise<void> {
  const { error } = await supabase.from("rooms").update(input).eq("id", roomId);
  if (error) throw error;
}

export async function archiveRoom(roomId: number): Promise<void> {
  const { error } = await supabase.from("rooms").update({ archived_at: new Date().toISOString() }).eq("id", roomId);
  if (error) throw error;
}

export interface EquipmentInput {
  site_id: number;
  room_id: number | null;
  manufacturer_id: number | null;
  equipment_model_id?: number | null;
  category: EquipmentRow["category"];
  model_number: string | null;
  serial_number: string | null;
  location_in_room: string | null;
  installed_on: string | null;
  notes: string | null;
  onsite_check_flagged?: boolean;
  onsite_check_note?: string | null;
}

export async function createEquipment(input: EquipmentInput): Promise<number> {
  const { data, error } = await supabase.from("equipment").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function updateEquipment(equipmentId: number, input: EquipmentInput): Promise<void> {
  const { error } = await supabase.from("equipment").update(input).eq("id", equipmentId);
  if (error) throw error;
}

export async function archiveEquipment(equipmentId: number): Promise<void> {
  const { error } = await supabase.from("equipment").update({ archived_at: new Date().toISOString() }).eq("id", equipmentId);
  if (error) throw error;
}

/**
 * Reconcile an equipment row against a matched Product Library profile via the
 * reconcile_equipment_to_profile RPC (0585): it resolves the catalogue
 * equipment_model for the profile — reusing the mapped one, adopting an
 * existing same-manufacturer+model row, or creating the canonical one — and
 * points the equipment at it, so the "not linked" flag clears every time
 * rather than dead-ending when no catalogue row happened to exist. Server-side
 * and SECURITY DEFINER because equipment_models is admin-gated; the RPC itself
 * checks the caller's can_edit_rooms. Returns the equipment_model_id.
 */
export async function reconcileEquipmentToProfile(equipmentId: number, profileId: number): Promise<number> {
  const { data, error } = await supabase.rpc("reconcile_equipment_to_profile", {
    p_equipment_id: equipmentId,
    p_profile_id: profileId,
  });
  // Surface the server's message (e.g. "This product has no manufacturer and
  // model to catalogue…") — throwing the raw PostgrestError object rendered
  // as an unreadable "[object Object]" in the Match panel.
  if (error) throw new Error(error.message);
  return Number(data);
}

/**
 * Add a fuzzy-matched product-library item to a room's equipment list
 * (feature: per-room fuzzy match): create the equipment row so it shows up in
 * the room's checklist, then reconcile it to the catalogue so it comes in
 * linked, not pre-flagged. The reconcile brings its make/model into line with
 * the matched product; a profile with no manufacturer/model to catalogue (rare)
 * leaves the item added but flagged rather than failing the add. Returns the
 * new equipment id.
 */
export async function addProductToRoomEquipment(opts: {
  siteId: number;
  roomId: number;
  candidate: ProductCandidate;
}): Promise<number> {
  const equipmentId = await createEquipment({
    site_id: opts.siteId,
    room_id: opts.roomId,
    manufacturer_id: null,
    category: "other",
    model_number: opts.candidate.model ?? opts.candidate.label ?? null,
    serial_number: null,
    location_in_room: null,
    installed_on: null,
    notes: opts.candidate.label ? `Matched from product library: ${opts.candidate.label}` : null,
  });
  try {
    await reconcileEquipmentToProfile(equipmentId, opts.candidate.profile_id);
  } catch {
    // Non-fatal: the item is added (and named). Only a profile with no
    // manufacturer/model to catalogue lands here, and it stays flagged — the
    // same outcome as before, not a failed add.
  }
  return equipmentId;
}

/**
 * Reconcile an EXISTING equipment row against a matched Product Library
 * profile — the "Match" action the unlinked-item warning points at. Links the
 * item to the catalogue equipment_model (find-or-create, clearing its ⚠) and
 * aligns its make/model to the matched product. Returns the equipment_model_id.
 */
export async function linkEquipmentToProduct(equipmentId: number, candidate: ProductCandidate): Promise<number> {
  return reconcileEquipmentToProfile(equipmentId, candidate.profile_id);
}

// --- Catalogue: manufacturers -------------------------------------------

export interface ManufacturerRow {
  id: number;
  name: string;
  website: string | null;
  support_url: string | null;
  support_tel: string | null;
  notes: string | null;
}
export interface ManufacturerInput {
  name: string;
  website: string | null;
  support_url: string | null;
  support_tel: string | null;
  notes: string | null;
}

export async function fetchManufacturers(): Promise<ManufacturerRow[]> {
  const { data, error } = await supabase
    .from("manufacturers")
    .select("id, name, website, support_url, support_tel, notes")
    .order("name");
  if (error) throw error;
  return (data ?? []) as ManufacturerRow[];
}

export async function createManufacturer(input: ManufacturerInput): Promise<number> {
  const { data, error } = await supabase.from("manufacturers").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function updateManufacturer(id: number, input: ManufacturerInput): Promise<void> {
  const { error } = await supabase.from("manufacturers").update(input).eq("id", id);
  if (error) throw error;
}

// --- Catalogue: equipment models ----------------------------------------

/** A Product Library profile, trimmed to what a picker/badge needs. */
export interface ProfileBrief {
  id: number;
  model: string | null;
  manufacturer_id?: number | null;
  manufacturer_name: string | null;
  short_description: string | null;
  category?: string | null;
  is_kit?: boolean;
}

export interface EquipmentModelRow {
  id: number;
  manufacturer_id: number;
  manufacturer_name: string | null;
  model_number: string;
  category: EquipmentCategory;
  description: string | null;
  notes: string | null;
  // Product Library reconciliation (migrations 0168 / 0169 / 0575).
  // av_product_profile_id is the confirmed canonical link; the suggested_*
  // pair is an unconfirmed fuzzy/AI proposal awaiting a human decision.
  av_product_profile_id: number | null;
  profile_match_confidence: number | null;
  profile_matched_via: string | null;
  suggested_av_product_profile_id: number | null;
  suggested_confidence: number | null;
  pending_revision_id: number | null;
  matched_profile: ProfileBrief | null;
  suggested_profile: ProfileBrief | null;
}
export interface EquipmentModelInput {
  manufacturer_id: number;
  model_number: string;
  category: EquipmentCategory;
  description: string | null;
  notes: string | null;
}

export async function fetchProfileBriefs(ids: number[]): Promise<Map<number, ProfileBrief>> {
  const map = new Map<number, ProfileBrief>();
  const unique = Array.from(new Set(ids));
  if (unique.length === 0) return map;
  const { data, error } = await supabase
    .from("av_product_profiles")
    .select("id, model, short_description, is_kit, manufacturers(name)")
    .in("id", unique);
  if (error) throw error;
  for (const r of (data ?? []) as unknown as Array<{
    id: number;
    model: string | null;
    short_description: string | null;
    is_kit: boolean | null;
    manufacturers: { name: string } | null;
  }>) {
    map.set(r.id, {
      id: r.id,
      model: r.model,
      short_description: r.short_description,
      is_kit: r.is_kit ?? undefined,
      manufacturer_name: r.manufacturers?.name ?? null,
    });
  }
  return map;
}

export async function fetchEquipmentModels(): Promise<EquipmentModelRow[]> {
  const { data, error } = await supabase
    .from("equipment_models")
    .select(
      "id, manufacturer_id, model_number, category, description, notes, av_product_profile_id, profile_match_confidence, profile_matched_via, suggested_av_product_profile_id, suggested_confidence, pending_revision_id, manufacturers(name)",
    )
    .order("model_number");
  if (error) throw error;
  const raw = (data ?? []) as unknown as Array<
    Omit<EquipmentModelRow, "manufacturer_name" | "matched_profile" | "suggested_profile"> & {
      manufacturers: { name: string } | null;
    }
  >;
  const briefs = await fetchProfileBriefs(
    raw.flatMap((r) => [r.av_product_profile_id, r.suggested_av_product_profile_id]).filter((x): x is number => x != null),
  );
  return raw.map((r) => {
    const { manufacturers, ...rest } = r;
    return {
      ...rest,
      manufacturer_name: manufacturers?.name ?? null,
      matched_profile: r.av_product_profile_id != null ? briefs.get(r.av_product_profile_id) ?? null : null,
      suggested_profile:
        r.suggested_av_product_profile_id != null ? briefs.get(r.suggested_av_product_profile_id) ?? null : null,
    };
  });
}

/** Fuzzy search of the Product Library for the "Find in Library" picker.
 * Reuses the same permission-gated RPC the quote/rack pickers use. */
export async function searchProductProfiles(query: string): Promise<ProfileBrief[]> {
  const q = query.trim();
  if (!q) return [];
  const { data, error } = await supabase.rpc("av_search_profiles", { p_query: q, p_status: null });
  if (error) throw error;
  const rows: ProfileBrief[] = ((data ?? []) as unknown as Array<{
    id: number;
    model: string | null;
    short_description: string | null;
    is_kit: boolean | null;
    manufacturer_id: number | null;
    category: string | null;
  }>).map((p) => ({
    id: p.id,
    model: p.model,
    short_description: p.short_description,
    is_kit: p.is_kit ?? undefined,
    manufacturer_id: p.manufacturer_id,
    category: p.category,
    manufacturer_name: null,
  }));
  // av_search_profiles returns SETOF av_product_profiles, which carries
  // manufacturer_id but not the name (it's a FK to manufacturers). Resolve the
  // handful of distinct ids so pickers can read "Manufacturer — Model" instead
  // of a bare model string (some of which embed the make inconsistently).
  const ids = Array.from(new Set(rows.map((r) => r.manufacturer_id).filter((x): x is number => x != null)));
  if (ids.length > 0) {
    const { data: mfrs, error: mErr } = await supabase.from("manufacturers").select("id, name").in("id", ids);
    if (mErr) throw mErr;
    const names = new Map((mfrs ?? []).map((m) => [m.id as number, m.name as string]));
    for (const r of rows) r.manufacturer_name = r.manufacturer_id != null ? names.get(r.manufacturer_id) ?? null : null;
  }
  return rows;
}

/** Canonical "Manufacturer — Model" label for a Product Library profile, so
 * every picker and reconciliation row reads the same way instead of a bare
 * model string (some of which embed the make inconsistently). Falls back to
 * the short description, then #id; a missing profile renders as "—". */
export function profileLabel(
  p: Pick<ProfileBrief, "id" | "model" | "manufacturer_name" | "short_description"> | null,
): string {
  if (!p) return "—";
  return [p.manufacturer_name, p.model].filter(Boolean).join(" — ") || p.short_description || `#${p.id}`;
}

/** The equipment_models row (if any) that already maps to a Product Library
 * profile — lets a new equipment item carry a canonical equipment_model_id
 * rather than a typed-in model. */
export async function fetchEquipmentModelIdForProfile(profileId: number): Promise<number | null> {
  const { data, error } = await supabase
    .from("equipment_models")
    .select("id")
    .eq("av_product_profile_id", profileId)
    .limit(1);
  if (error) throw error;
  return data?.[0]?.id ?? null;
}

export async function createEquipmentModel(input: EquipmentModelInput): Promise<number> {
  const { data, error } = await supabase.from("equipment_models").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function updateEquipmentModel(id: number, input: EquipmentModelInput): Promise<void> {
  const { error } = await supabase.from("equipment_models").update(input).eq("id", id);
  if (error) throw error;
}

// --- Product Library reconciliation actions -----------------------------
// Direct row updates (RLS-gated the same as updateEquipmentModel). Confirming
// promotes the suggestion to the canonical link; a human decision, so
// profile_matched_via becomes 'manual'.

export async function confirmModelSuggestion(id: number, profileId: number, confidence: number | null): Promise<void> {
  const { error } = await supabase
    .from("equipment_models")
    .update({
      av_product_profile_id: profileId,
      profile_matched_via: "manual",
      profile_match_confidence: confidence,
      profile_matched_at: new Date().toISOString(),
      suggested_av_product_profile_id: null,
      suggested_confidence: null,
    })
    .eq("id", id);
  if (error) throw error;
}

export async function rejectModelSuggestion(id: number): Promise<void> {
  const { error } = await supabase
    .from("equipment_models")
    .update({ suggested_av_product_profile_id: null, suggested_confidence: null })
    .eq("id", id);
  if (error) throw error;
}

export async function setModelProfile(id: number, profileId: number): Promise<void> {
  const { error } = await supabase
    .from("equipment_models")
    .update({
      av_product_profile_id: profileId,
      profile_matched_via: "manual",
      profile_match_confidence: null,
      profile_matched_at: new Date().toISOString(),
      suggested_av_product_profile_id: null,
      suggested_confidence: null,
    })
    .eq("id", id);
  if (error) throw error;
}

export async function unlinkModelProfile(id: number): Promise<void> {
  const { error } = await supabase
    .from("equipment_models")
    .update({
      av_product_profile_id: null,
      profile_matched_via: null,
      profile_match_confidence: null,
      profile_matched_at: null,
    })
    .eq("id", id);
  if (error) throw error;
}

// --- Global equipment browse --------------------------------------------

export interface EquipmentCatalogueRow extends EquipmentRow {
  site_name: string | null;
  company_id: number | null;
  company_name: string | null;
  room_name: string | null;
  equipment_model_number: string | null;
}

export async function fetchEquipmentCatalogue(opts?: {
  companyId?: number;
  manufacturerId?: number;
  modelId?: number;
  search?: string;
}): Promise<EquipmentCatalogueRow[]> {
  let q = supabase
    .from("equipment")
    .select(
      "*, sites!inner(name, company_id, companies(name)), rooms(name), manufacturers(name), equipment_models(model_number)",
    )
    .is("archived_at", null)
    .limit(500);
  if (opts?.manufacturerId) q = q.eq("manufacturer_id", opts.manufacturerId);
  if (opts?.modelId) q = q.eq("equipment_model_id", opts.modelId);
  if (opts?.companyId) q = q.eq("sites.company_id", opts.companyId);
  if (opts?.search) q = q.or(`model_number.ilike.%${opts.search}%,serial_number.ilike.%${opts.search}%`);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as unknown as Array<
    EquipmentRow & {
      sites: { name: string; company_id: number; companies: { name: string } | null } | null;
      rooms: { name: string } | null;
      manufacturers: { name: string } | null;
      equipment_models: { model_number: string } | null;
    }
  >).map((r) => {
    const { sites, rooms, manufacturers, equipment_models, ...rest } = r;
    return {
      ...rest,
      manufacturer_name: manufacturers?.name ?? null,
      site_name: sites?.name ?? null,
      company_id: sites?.company_id ?? null,
      company_name: sites?.companies?.name ?? null,
      room_name: rooms?.name ?? null,
      equipment_model_number: equipment_models?.model_number ?? null,
    };
  });
}

/* ------------------------------------------------------------ room hub */

export async function fetchRoomDetail(roomId: number): Promise<RoomAv | null> {
  const { data, error } = await supabase.from("v_rooms_av").select("*").eq("id", roomId).maybeSingle();
  if (error) throw error;
  return (data as RoomAv | null) ?? null;
}

export interface RoomVisitHistoryRow {
  visit_id: number;
  visit_reference: string;
  visit_type: "reactive" | "preventative";
  visit_date: string | null;
  engineer_name: string | null;
  faults_found: boolean;
  fault_repaired: boolean;
}

/** service_visit_rooms.room_id has had a dedicated index since 0005 but
 * nothing ever queried it -- a room's own visit history was only ever
 * reachable by going the other way, through the visit itself. */
export async function fetchRoomVisitHistory(roomId: number): Promise<RoomVisitHistoryRow[]> {
  const { data, error } = await supabase
    .from("service_visit_rooms")
    .select("visit_id, faults_found, fault_repaired, profiles(full_name), service_visits(reference, visit_type, visit_date)")
    .eq("room_id", roomId);
  if (error) throw error;
  return (
    (data ?? []) as unknown as Array<{
      visit_id: number;
      faults_found: boolean;
      fault_repaired: boolean;
      profiles: { full_name: string } | null;
      service_visits: { reference: string; visit_type: "reactive" | "preventative"; visit_date: string | null } | null;
    }>
  )
    .filter((r) => r.service_visits != null)
    .map((r) => ({
      visit_id: r.visit_id,
      visit_reference: r.service_visits!.reference,
      visit_type: r.service_visits!.visit_type,
      visit_date: r.service_visits!.visit_date,
      engineer_name: r.profiles?.full_name ?? null,
      faults_found: r.faults_found,
      fault_repaired: r.fault_repaired,
    }))
    .sort((a, b) => (b.visit_date ?? "").localeCompare(a.visit_date ?? ""));
}

export interface RoomFileRow {
  id: number;
  file_id: number;
  image_kind: "room" | "rack";
  is_primary: boolean;
  storage_path: string;
  created_at: string;
}

export async function fetchRoomFiles(roomId: number): Promise<RoomFileRow[]> {
  const { data, error } = await supabase
    .from("room_files")
    .select("id, file_id, image_kind, is_primary, created_at, files(storage_path)")
    .eq("room_id", roomId)
    .order("created_at");
  if (error) throw error;
  return (
    (data ?? []) as unknown as Array<{
      id: number;
      file_id: number;
      image_kind: "room" | "rack";
      is_primary: boolean;
      created_at: string;
      files: { storage_path: string } | null;
    }>
  )
    .filter((r) => r.files != null)
    .map((r) => ({
      id: r.id,
      file_id: r.file_id,
      image_kind: r.image_kind,
      is_primary: r.is_primary,
      created_at: r.created_at,
      storage_path: r.files!.storage_path,
    }));
}

export async function uploadRoomImage(
  roomId: number,
  file: File,
  imageKind: "room" | "rack",
  uploaderId: string,
): Promise<number> {
  const fileId = await uploadEvidencePhoto("room-photos", `rooms/${roomId}/${imageKind}`, file, uploaderId);
  const { data, error } = await supabase
    .from("room_files")
    .insert({ room_id: roomId, file_id: fileId, image_kind: imageKind })
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

export async function deleteRoomFile(roomFileId: number): Promise<void> {
  const { error } = await supabase.from("room_files").delete().eq("id", roomFileId);
  if (error) throw error;
}

export async function signedRoomPhotoUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage.from("room-photos").createSignedUrl(storagePath, 300);
  if (error) throw error;
  return data.signedUrl;
}

export async function fetchContractsForCompany(companyId: number): Promise<ContractOption[]> {
  const { data, error } = await supabase
    .from("v_contracts_display")
    .select("id, reference, cover_level")
    .eq("company_id", companyId)
    .is("archived_at", null);
  if (error) throw error;
  return (data ?? []) as ContractOption[];
}

/** The contract covering a given site, if any -- every site with a
 * contract_sites link has exactly one (checked live: 152/152, zero with
 * more than one), so a New Visit's Contract can be safely auto-filled
 * from Site instead of asking the user to reselect what the site already
 * implies. Returns null for the small remainder of sites with no
 * contract at all, which is a legitimate "no cover" state, not a bug. */
export async function fetchContractIdForSite(siteId: number): Promise<number | null> {
  const { data: links, error: linksErr } = await supabase
    .from("contract_sites")
    .select("contract_id")
    .eq("site_id", siteId);
  if (linksErr) throw linksErr;
  const contractIds = (links ?? []).map((r) => r.contract_id as number);
  if (contractIds.length === 0) return null;
  const { data, error } = await supabase
    .from("v_contracts_display")
    .select("id")
    .in("id", contractIds)
    .is("archived_at", null);
  if (error) throw error;
  return data?.[0]?.id ?? null;
}

export async function fetchEngineers(): Promise<EngineerOption[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name")
    .eq("is_active", true)
    .order("full_name");
  if (error) throw error;
  return (data ?? []) as EngineerOption[];
}

/* ---------------------------------------------------------- site detail */

export interface KitList {
  site_id: number;
  file_id: number | null;
  kit_value: number | null;
  notes: string | null;
  storage_path: string | null;
}

export interface SiteDetailData {
  site: Site;
  company: { id: number; name: string };
  leadContact: Contact | null;
  contracts: Contract[];
  kitList: KitList | null;
}

/**
 * Bounded data only (site, company, lead contact, linked contracts, kit
 * list) — the unbounded tabs (Visits/Tickets/Phone Log) load lazily per
 * tab via their own paginated/filtered fetchers, same as the top-level
 * Visits list does, rather than being pulled into this one call.
 */
export async function fetchSiteDetail(siteId: number): Promise<SiteDetailData> {
  const { data: site, error: siteErr } = await supabase.from("sites").select("*").eq("id", siteId).maybeSingle();
  if (siteErr) throw siteErr;
  if (!site) throw new Error("Site not found");

  const [companyRes, contactRes, linksRes, kitRes] = await Promise.all([
    supabase.from("companies").select("id, name").eq("id", site.company_id).maybeSingle(),
    site.lead_contact_id
      ? supabase.from("contacts").select("*").eq("id", site.lead_contact_id).maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    supabase.from("contract_sites").select("contract_id").eq("site_id", siteId),
    supabase.from("site_kit_lists").select("*, files(storage_path)").eq("site_id", siteId).maybeSingle(),
  ]);
  if (companyRes.error) throw companyRes.error;
  if (contactRes.error) throw contactRes.error;
  if (linksRes.error) throw linksRes.error;
  if (kitRes.error) throw kitRes.error;

  const contractIds = (linksRes.data ?? []).map((r) => r.contract_id as number);
  let contracts: Contract[] = [];
  if (contractIds.length > 0) {
    // v_contracts_display, not the bare table — value/job_number must stay
    // masked for anyone without can_view_sla_costs, same as CompanyDetail.
    const { data, error } = await supabase.from("v_contracts_display").select("*").in("id", contractIds);
    if (error) throw error;
    contracts = (data ?? []) as Contract[];
  }

  const kit = kitRes.data as (KitList & { files: { storage_path: string } | null }) | null;

  return {
    site: site as Site,
    company: companyRes.data as { id: number; name: string },
    leadContact: (contactRes.data as Contact | null) ?? null,
    contracts,
    kitList: kit
      ? { site_id: kit.site_id, file_id: kit.file_id, kit_value: kit.kit_value, notes: kit.notes, storage_path: kit.files?.storage_path ?? null }
      : null,
  };
}

export async function fetchRoomsAvForSite(siteId: number): Promise<RoomAv[]> {
  const { data, error } = await supabase.from("v_rooms_av").select("*").eq("site_id", siteId).order("name");
  if (error) throw error;
  return (data ?? []) as RoomAv[];
}

/* ------------------------------------------------------------ phone log */

export interface PhoneCallRow {
  id: number;
  company_id: number | null;
  company_name: string | null;
  site_id: number | null;
  site_name: string | null;
  room_id: number | null;
  engineer_id: string | null;
  engineer_name: string | null;
  called_at: string;
  caller_name: string | null;
  summary: string | null;
  action_taken: string | null;
  resolved: boolean;
  notes: string | null;
}

interface PhoneCallJoinRow {
  id: number;
  company_id: number | null;
  site_id: number | null;
  room_id: number | null;
  engineer_id: string | null;
  called_at: string;
  caller_name: string | null;
  summary: string | null;
  action_taken: string | null;
  resolved: boolean;
  notes: string | null;
  companies: { name: string } | null;
  sites: { name: string } | null;
  profiles: { full_name: string } | null;
}

function mapPhoneCallRow(r: PhoneCallJoinRow): PhoneCallRow {
  return {
    id: r.id,
    company_id: r.company_id,
    company_name: r.companies?.name ?? null,
    site_id: r.site_id,
    site_name: r.sites?.name ?? null,
    room_id: r.room_id,
    engineer_id: r.engineer_id,
    engineer_name: r.profiles?.full_name ?? null,
    called_at: r.called_at,
    caller_name: r.caller_name,
    summary: r.summary,
    action_taken: r.action_taken,
    resolved: r.resolved,
    notes: r.notes,
  };
}

export async function fetchPhoneCalls(filter?: { site_id?: number; resolved?: boolean }): Promise<PhoneCallRow[]> {
  let q = supabase
    .from("phone_calls")
    .select("*, companies(name), sites(name), profiles(full_name)")
    .order("called_at", { ascending: false });
  if (filter?.site_id) q = q.eq("site_id", filter.site_id);
  if (filter?.resolved !== undefined) q = q.eq("resolved", filter.resolved);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as unknown as PhoneCallJoinRow[]).map(mapPhoneCallRow);
}

export interface PhoneCallInput {
  company_id: number | null;
  site_id: number | null;
  room_id: number | null;
  engineer_id: string | null;
  caller_name: string | null;
  summary: string | null;
  action_taken: string | null;
  resolved: boolean;
  notes: string | null;
}

export async function createPhoneCall(input: PhoneCallInput): Promise<void> {
  const { error } = await supabase.from("phone_calls").insert(input);
  if (error) throw new Error(error.message);
}

export async function updatePhoneCall(id: number, input: PhoneCallInput): Promise<void> {
  const { error } = await supabase.from("phone_calls").update(input).eq("id", id);
  if (error) throw new Error(error.message);
}

/** Quick open/close toggle from the log row, without opening the full editor. */
export async function setPhoneCallResolved(id: number, resolved: boolean): Promise<void> {
  const { error } = await supabase.from("phone_calls").update({ resolved }).eq("id", id);
  if (error) throw new Error(error.message);
}

/* --------------------------------------------------------- client offices */

export interface SiteListRow {
  site_id: number;
  site_name: string;
  company_id: number;
  company_name: string;
  city: string | null;
  pm_next_visit_booked_at: string | null;
  pm_client_confirmed: boolean;
  last_pm_visit: string | null;
  pm_visit_status: "never_visited" | "overdue" | "due_soon" | "current" | "no_contract";
  next_pm_due: string | null;
}

export async function fetchSitesList(): Promise<SiteListRow[]> {
  const { data, error } = await supabase.from("v_pm_detail").select("*").order("company_name").order("site_name");
  if (error) throw error;
  return (data ?? []) as SiteListRow[];
}

/* -------------------------------------------------------------- contracts */

export interface LiveContractRow {
  id: number;
  reference: string | null;
  company_id: number;
  company_name: string;
  /** For the Logo.dev lookup (lib/logo.ts) — no need to fetch companies
   * separately just to get this. */
  company_website: string | null;
  cover_level: "gold" | "silver" | "bronze";
  /** Null unless the caller holds can_view_sla_costs. */
  contract_value: number | null;
  start_date: string | null;
  end_date: string | null;
  days_until_expiry: number | null;
  contract_status: "unknown" | "expired" | "expiring_soon" | "renewal_due" | "live";
  not_renewing: boolean;
  /** Why, if given — 0425. Optional even when not_renewing is true. */
  not_renewing_reason: string | null;
  last_chase_sent_at: string | null;
  total_equipment_value: number;
  renewal_contact_name: string | null;
  renewal_contact_email: string | null;
  notes: string | null;
  /** Null unless the caller holds can_view_sla_costs (0179). */
  job_number: string | null;
  length_years: number | null;
  sla_exclusions: string | null;
  renewal_contact_id: number | null;
  /** Linked sites' towns (sites.city, or the site name where no town is
   * recorded), distinct and alphabetical, comma-joined — for the "where does
   * this cover apply" summary on the Live Contracts list. Null when no site
   * is linked yet (0583). */
  locations: string | null;
  location_count: number;
}

export async function fetchLiveContract(contractId: number): Promise<LiveContractRow | null> {
  const { data, error } = await supabase.from("v_live_contracts").select("*").eq("id", contractId).maybeSingle();
  if (error) throw error;
  return (data as LiveContractRow | null) ?? null;
}

/** Same 90-day-out / 60-day-lapsed window as the v_contracts_to_chase view
 * the Dashboard's "Contracts to chase" count reads — replayed client-side
 * so this list can filter to it without a second round trip. */
export function contractNeedsChase(c: LiveContractRow): boolean {
  return !c.not_renewing && c.days_until_expiry != null && c.days_until_expiry >= -60 && c.days_until_expiry <= 90;
}

export async function fetchLiveContracts(): Promise<LiveContractRow[]> {
  const { data, error } = await supabase
    .from("v_live_contracts")
    .select("*")
    .order("end_date", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data ?? []) as LiveContractRow[];
}

/* -------------------------------------------------- dashboard financials */

export interface ServiceFinancials {
  live_contracts: number;
  /** Null unless the caller holds can_view_sla_costs. */
  annual_sla_value: number | null;
  total_equipment_value: number;
  /** False when site_kit_lists is empty — lets the UI say "not uploaded yet" instead of a misleading £0. */
  has_kit_lists: boolean;
  gold_count: number;
  silver_count: number;
  bronze_count: number;
  expiring_90d: number;
  contracts_without_site: number;
  sites_covered: number;
  rooms_covered: number;
}

export async function fetchServiceFinancials(): Promise<ServiceFinancials> {
  const { data, error } = await supabase.from("v_service_financials").select("*").single();
  if (error) throw error;
  return data as ServiceFinancials;
}

export interface EngineerVisitCount {
  engineer_name: string;
  count: number;
}

/** Calendar-month cutoff (not a fixed day-count), matching Glide's own
 * `CURRENT_DATE - INTERVAL '12 months'` exactly rather than approximating
 * it as N*86400 seconds. Exported so the exact reconciled window can be
 * unit-tested without hitting the database. */
export function monthsAgoDateString(monthsBack: number, from: Date = new Date()): string {
  const cutoffDate = new Date(from);
  cutoffDate.setMonth(cutoffDate.getMonth() - monthsBack);
  return cutoffDate.toISOString().slice(0, 10);
}

/** Bounded to the trailing `monthsBack` window (12 months, matching the
 * live Glide dashboard's own "Last 12 Months" PM/Reactive charts exactly —
 * confirmed by reading Glide's server.ts, not assumed), filtered
 * server-side — same lightweight-aggregation shape as fetchDashboard's
 * other counts, not a whole-table fetch. */
export async function fetchVisitsByEngineer(
  monthsBack = 12,
): Promise<{ reactive: EngineerVisitCount[]; preventative: EngineerVisitCount[] }> {
  const cutoff = monthsAgoDateString(monthsBack);
  const { data, error } = await supabase
    .from("v_service_visits_list")
    .select("visit_type, engineer_name")
    .gte("visit_date", cutoff);
  if (error) throw error;
  const rows = (data ?? []) as { visit_type: "reactive" | "preventative"; engineer_name: string | null }[];

  const tally = (type: "reactive" | "preventative"): EngineerVisitCount[] => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (r.visit_type !== type) continue;
      const name = r.engineer_name ?? "Unassigned";
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([engineer_name, count]) => ({ engineer_name, count }))
      .sort((a, b) => b.count - a.count);
  };

  return { reactive: tally("reactive"), preventative: tally("preventative") };
}

export interface ContractSnapshotRow {
  snapshot_date: string;
  /** Masked to null for callers without can_view_sla_costs (see v_contract_snapshots_display, migration 0176). */
  total_value: number | null;
  /** Null before 2025-02-17 -- Glide itself never recorded a contract
   * count that early, not something Core failed to capture. */
  contract_count: number | null;
}

export async function fetchContractSnapshots(): Promise<ContractSnapshotRow[]> {
  const { data, error } = await supabase
    .from("v_contract_snapshots_display")
    .select("snapshot_date, total_value, contract_count")
    .order("snapshot_date");
  if (error) throw error;
  return (data ?? []) as ContractSnapshotRow[];
}

/* ------------------------------------------------------------- renewals */

/** Reverse-engineered from a live contract (kit value × rate = contract
 * value) and confirmed with the business. Was the only source of these
 * rates; now just the fallback default if renewal_rate_tiers (0165) is
 * ever empty — fetchRenewalRateTiers() is the live, business-editable
 * source everything else should use. */
export const RENEWAL_RATES: Record<"bronze" | "silver" | "gold", number> = {
  bronze: 0.06,
  silver: 0.10,
  gold: 0.14,
};

export interface RenewalQuoteRow {
  id: number;
  contract_id: number;
  reference: string | null;
  /** Null unless the caller holds can_view_sla_costs. */
  quoted_value: number | null;
  new_start_date: string | null;
  new_end_date: string | null;
  status: string;
  file_id: number | null;
  storage_path: string | null;
  sent_at: string | null;
  notes: string | null;
  created_at: string;
  company_name: string;
  contract_reference: string | null;
  cover_level: "gold" | "silver" | "bronze";
}

export async function fetchRenewalQuotes(filter?: { contract_id?: number }): Promise<RenewalQuoteRow[]> {
  // NB: read the view WITHOUT embedding contracts/files. v_renewal_quotes_display
  // is a security_definer view, and PostgREST embedding through it is unreliable
  // (a failed embed 400s the whole request, which showed up as an empty "Saved
  // Quotes" list). Fetch the related rows by id and stitch them in instead.
  let query = supabase
    .from("v_renewal_quotes_display")
    .select("*")
    .order("created_at", { ascending: false });
  if (filter?.contract_id != null) query = query.eq("contract_id", filter.contract_id);
  const { data, error } = await query;
  if (error) throw error;
  const rows = (data ?? []) as unknown as Array<{
    id: number;
    contract_id: number;
    reference: string | null;
    quoted_value: number | null;
    new_start_date: string | null;
    new_end_date: string | null;
    status: RenewalQuoteRow["status"];
    file_id: number | null;
    sent_at: string | null;
    notes: string | null;
    created_at: string;
  }>;

  const contractIds = Array.from(new Set(rows.map((r) => r.contract_id).filter((x): x is number => x != null)));
  const fileIds = Array.from(new Set(rows.map((r) => r.file_id).filter((x): x is number => x != null)));

  const contractsById = new Map<number, { reference: string | null; cover_level: "gold" | "silver" | "bronze"; company_name: string | null }>();
  if (contractIds.length > 0) {
    const { data: cdata, error: cerr } = await supabase
      .from("contracts")
      .select("id, reference, cover_level, companies(name)")
      .in("id", contractIds);
    if (cerr) throw cerr;
    for (const c of (cdata ?? []) as unknown as Array<{ id: number; reference: string | null; cover_level: "gold" | "silver" | "bronze"; companies: { name: string } | null }>) {
      contractsById.set(c.id, { reference: c.reference, cover_level: c.cover_level, company_name: c.companies?.name ?? null });
    }
  }

  const pathByFile = new Map<number, string | null>();
  if (fileIds.length > 0) {
    const { data: fdata, error: ferr } = await supabase.from("files").select("id, storage_path").in("id", fileIds);
    if (ferr) throw ferr;
    for (const f of (fdata ?? []) as unknown as Array<{ id: number; storage_path: string | null }>) {
      pathByFile.set(f.id, f.storage_path ?? null);
    }
  }

  return rows.map((r) => {
    const c = r.contract_id != null ? contractsById.get(r.contract_id) : undefined;
    return {
      id: r.id,
      contract_id: r.contract_id,
      reference: r.reference,
      quoted_value: r.quoted_value,
      new_start_date: r.new_start_date,
      new_end_date: r.new_end_date,
      status: r.status,
      file_id: r.file_id,
      storage_path: r.file_id != null ? pathByFile.get(r.file_id) ?? null : null,
      sent_at: r.sent_at,
      notes: r.notes,
      created_at: r.created_at,
      company_name: c?.company_name ?? "Unknown company",
      contract_reference: c?.reference ?? null,
      cover_level: c?.cover_level ?? "bronze",
    };
  });
}

export interface SiteKitValueRow {
  site_id: number;
  site_name: string;
  kit_value: number;
}

/** Per-site kit values for every site linked to a contract — the
 * "Equipment Values by Site" table in the renewal generator. Each site's
 * saved kit_value is the default; the renewal dialog itself lets the
 * estimator override a value for calculation purposes only, without
 * writing back to site_kit_lists (per the brief's explicit warning not to
 * conflate a renewal-time override with the persisted kit list value). */
export async function fetchContractSiteKitValues(contractId: number): Promise<SiteKitValueRow[]> {
  const sites = await fetchContractSites(contractId);
  if (sites.length === 0) return [];
  const { data, error } = await supabase.from("site_kit_lists").select("site_id, kit_value").in("site_id", sites.map((s) => s.id));
  if (error) throw error;
  const values = new Map((data ?? []).map((r) => [r.site_id as number, (r.kit_value as number | null) ?? 0]));
  return sites.map((s) => ({ site_id: s.id, site_name: s.name, kit_value: values.get(s.id) ?? 0 }));
}

export interface ContractKitValue {
  total: number;
  siteCount: number;
}

/** Sums kit value across every site actually linked to the contract — a
 * contract can cover several offices, so renewal pricing shouldn't be
 * based on just the one site the generator happened to be opened from. */
export async function fetchContractKitValue(contractId: number): Promise<ContractKitValue> {
  const { data: links, error: linksErr } = await supabase
    .from("contract_sites")
    .select("site_id")
    .eq("contract_id", contractId);
  if (linksErr) throw linksErr;
  const siteIds = (links ?? []).map((r) => r.site_id as number);
  if (siteIds.length === 0) return { total: 0, siteCount: 0 };

  const { data, error } = await supabase.from("site_kit_lists").select("kit_value").in("site_id", siteIds);
  if (error) throw error;
  const total = (data ?? []).reduce((sum, r) => sum + (r.kit_value ?? 0), 0);
  return { total, siteCount: siteIds.length };
}

export interface GenerateRenewalInput {
  contractId: number;
  newStartDate: string;
  newEndDate: string;
  quotedValue: number;
  siteBreakdown: unknown;
}

/**
 * quoted_value/site_breakdown go through app.create_renewal_quote, not a
 * direct insert -- the pricing math still runs client-side (renewalCalc.ts),
 * but persisting the result requires can_view_sla_costs as well as
 * can_edit_contracts (migration 0178: a direct table insert of these two
 * columns is no longer possible for `authenticated` at all, closing the
 * same write-masking-bypass class fixed for contracts.value in 0177).
 *
 * The quote is saved as 'generated' (migration 0603) and its document is
 * rendered on demand by RenewalDocument (/service/renewal-quote/view). There
 * is no longer a server-side PDF step: the generate-renewal-quote edge
 * function and its pdf-lib document were retired once the branded, printable
 * RenewalDocument replaced them.
 */
export async function generateRenewalQuote(input: GenerateRenewalInput): Promise<{ id: number }> {
  const { data: id, error } = await supabase.rpc("create_renewal_quote", {
    p_contract_id: input.contractId,
    p_quoted_value: input.quotedValue,
    p_new_start_date: input.newStartDate,
    p_new_end_date: input.newEndDate,
    p_site_breakdown: input.siteBreakdown,
  });
  if (error) throw error;
  return { id: id as number };
}

/* -------------------------------------------------------------- writes */

export interface TicketInput {
  subject: string;
  description: string | null;
  company_id: number | null;
  site_id: number | null;
  room_id: number | null;
  contract_id: number | null;
  priority: TicketPriority;
  requester_name: string | null;
  requester_email: string | null;
  requester_phone: string | null;
  assigned_to: string | null;
}

export async function createTicket(input: TicketInput): Promise<number> {
  const { data, error } = await supabase.from("tickets").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function addTicketMessage(input: {
  ticketId: number;
  body: string;
  direction: "outbound" | "internal";
  authorName: string;
}): Promise<void> {
  const { error } = await supabase.from("ticket_messages").insert({
    ticket_id: input.ticketId,
    direction: input.direction,
    body: input.body,
    author_name: input.authorName,
    sent_at: input.direction === "outbound" ? new Date().toISOString() : null,
  });
  if (error) throw error;
}

export async function assignTicket(ticketId: number, engineerId: string | null): Promise<void> {
  const { error } = await supabase.from("tickets").update({ assigned_to: engineerId }).eq("id", ticketId);
  if (error) throw error;
}

export async function setTicketStatus(ticketId: number, status: string): Promise<void> {
  const { error } = await supabase.rpc("ticket_set_status", { p_id: ticketId, p_status: status });
  if (error) throw error;
}

export async function resolveTicket(
  ticketId: number,
  summary: string,
  rootCause: string | null,
  timeSpentMinutes?: number | null,
): Promise<void> {
  const { error } = await supabase.rpc("ticket_resolve", {
    p_id: ticketId,
    p_summary: summary,
    p_root_cause: rootCause,
    p_time_spent_minutes: timeSpentMinutes ?? null,
  });
  if (error) throw error;
}

export async function closeTicket(ticketId: number): Promise<void> {
  const { error } = await supabase.rpc("ticket_close", { p_id: ticketId });
  if (error) throw error;
}

export async function reopenTicket(ticketId: number, note?: string | null): Promise<void> {
  const { error } = await supabase.rpc("ticket_reopen", { p_id: ticketId, p_note: note ?? null });
  if (error) throw error;
}

export interface TicketActivityRow {
  id: number;
  action: string;
  actor_name: string | null;
  summary: string | null;
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

/** Ticket audit timeline from activity_log (created/assigned/status_changed/
 * commented), newest first. */
export async function fetchTicketActivity(ticketId: number): Promise<TicketActivityRow[]> {
  const { data, error } = await supabase
    .from("activity_log")
    .select("id, action, summary, field, old_value, new_value, created_at, profiles(full_name)")
    .eq("entity_type", "ticket")
    .eq("entity_id", ticketId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data ?? []) as unknown as Array<TicketActivityRow & { profiles: { full_name: string } | null }>).map((r) => {
    const { profiles, ...rest } = r;
    return { ...rest, actor_name: profiles?.full_name ?? null };
  });
}

export interface TicketKpis {
  opened: number;
  resolved_or_closed: number;
  open: number;
  open_0_2d: number;
  open_3_7d: number;
  open_over_7d: number;
  reopened: number;
  breached_open: number;
  no_policy: number;
  avg_response_minutes: number | null;
  median_response_minutes: number | null;
  avg_resolution_minutes: number | null;
  median_resolution_minutes: number | null;
  response_compliance_pct: number | null;
  resolution_compliance_pct: number | null;
  converted_to_visit: number;
  by_priority: Record<string, number>;
  by_status: Record<string, number>;
  top_rooms: { room_id: number; count: number }[];
}

/** Client-health ticket KPIs for a company (null = global) over a created_at
 * window (inclusive). Powers the per-client dashboard tab + ops dashboard. */
export async function fetchTicketKpis(
  companyId: number | null,
  from?: string | null,
  to?: string | null,
): Promise<TicketKpis> {
  const { data, error } = await supabase.rpc("ticket_kpis", {
    p_company_id: companyId,
    p_from: from ?? null,
    p_to: to ?? null,
  });
  if (error) throw error;
  return data as TicketKpis;
}

export async function linkVisitToTicket(ticketId: number, visitId: number): Promise<void> {
  const { error } = await supabase.from("ticket_visit_links").insert({ ticket_id: ticketId, visit_id: visitId });
  if (error) throw error;
}

export interface VisitInput {
  company_id: number;
  site_id: number | null;
  contract_id: number | null;
  visit_type: "reactive" | "preventative";
  visit_date: string | null;
  engineer_id: string | null;
  notes: string | null;
}

export async function createVisit(input: VisitInput): Promise<number> {
  const { data, error } = await supabase.from("service_visits").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export interface VisitUpdateInput {
  visit_date: string | null;
  engineer_id: string | null;
  status: VisitRow["status"];
  notes: string | null;
  site_id?: number | null;
}

export async function updateVisit(visitId: number, input: VisitUpdateInput): Promise<void> {
  const { error } = await supabase.from("service_visits").update(input).eq("id", visitId);
  if (error) throw error;
}

/**
 * Hard delete -- service_visit_rooms/ticket_visit_links cascade, but
 * service_reports references visit_id with no ON DELETE action, so the
 * database itself refuses to delete a visit with an issued report (a
 * report is a permanent, already-issued record). Surface that refusal
 * as a clear message rather than trying to force it through.
 */
export async function deleteVisit(visitId: number): Promise<void> {
  const { error } = await supabase.from("service_visits").delete().eq("id", visitId);
  if (error) {
    if (error.code === "23503") {
      throw new Error(
        "This visit can't be deleted because it has an issued report, which is a permanent record. " +
          "Remove or reassign the report first if this really needs to be undone.",
      );
    }
    throw error;
  }
}

export interface SaveVisitRoomsResult {
  rooms: VisitRoom[];
  id_map: Record<string, number>;
  tickets_to_confirm: { id: number; ticket_number: string; subject: string }[];
  deleted_room_ids: number[];
}

/**
 * deletedRoomIds is an explicit id list, never delete-by-omission — a
 * room simply missing from `rooms` (a stale draft, a client bug, a
 * partial payload) is left alone. The RPC scopes deletion to this visit
 * only and logs the full pre-delete row to activity_log before removing
 * it, so what a deleted room contained is always reconstructable even
 * though the row itself is gone.
 */
export async function saveVisitRooms(
  visitId: number,
  rooms: VisitRoom[],
  resolveTicketIds: number[] = [],
  deletedRoomIds: number[] = [],
): Promise<SaveVisitRoomsResult> {
  const { data, error } = await supabase.rpc("save_visit_rooms", {
    p_visit_id: visitId,
    p_rooms: rooms,
    p_resolve_ticket_ids: resolveTicketIds,
    p_deleted_room_ids: deletedRoomIds,
  });
  if (error) throw error;
  return data as SaveVisitRoomsResult;
}

/**
 * Kept as a named export because callers outside this file use it. The
 * algorithm moved to lib/images.ts when the vehicle-document upload
 * needed the same thing at a different resolution -- two copies of a
 * resize would have drifted, and the one that drifted would have been
 * the one nobody was looking at.
 *
 * Same spec as before: 1600px long edge, JPEG q0.8, the figures the
 * Service migration plan carries over from the Glide app.
 */
export async function compressImage(file: File): Promise<Blob> {
  const { blob } = await compress(file, EVIDENCE_PROFILE);
  return blob;
}

/** Compresses, uploads to Storage, records a `files` row, returns its id. */
export async function uploadEvidencePhoto(
  bucket: "room-photos" | "fault-photos",
  pathPrefix: string,
  file: File,
  uploadedBy: string,
): Promise<number> {
  // The mime type comes back from the compressor because a file it
  // declined to re-encode is still whatever it was, and uploading a PNG
  // labelled as JPEG makes it unopenable later.
  const { blob, mimeType } = await compress(file, EVIDENCE_PROFILE);
  const ext = mimeType === "image/jpeg" ? "jpg" : (mimeType.split("/")[1] ?? "bin");
  const path = `${pathPrefix}/${Date.now()}-${Math.round(Math.random() * 1e6)}.${ext}`;
  const { error: upErr } = await supabase.storage.from(bucket).upload(path, blob, { contentType: mimeType });
  if (upErr) throw upErr;
  const { data, error } = await supabase
    .from("files")
    .insert({
      bucket_id: bucket,
      storage_path: path,
      mime_type: mimeType,
      size_bytes: blob.size,
      uploaded_by: uploadedBy,
    })
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

/**
 * Upload/replace the one kit-list file for a site (unique on site_id).
 * No image compression here — a kit list is an xlsx, not a photo.
 */
export async function uploadKitListFile(siteId: number, file: File, uploadedBy: string): Promise<void> {
  const ext = file.name.split(".").pop() ?? "xlsx";
  const path = `kit-lists/${siteId}/${Date.now()}-${Math.round(Math.random() * 1e6)}.${ext}`;
  const { error: upErr } = await supabase.storage
    .from("documents")
    .upload(path, file, { contentType: file.type || "application/octet-stream" });
  if (upErr) throw upErr;

  const { data: fileRow, error: fileErr } = await supabase
    .from("files")
    .insert({
      bucket_id: "documents",
      storage_path: path,
      mime_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      uploaded_by: uploadedBy,
    })
    .select("id")
    .single();
  if (fileErr) throw fileErr;

  const { error: upsertErr } = await supabase
    .from("site_kit_lists")
    .upsert({ site_id: siteId, file_id: fileRow.id }, { onConflict: "site_id" });
  if (upsertErr) throw upsertErr;
}

export async function updateKitListValue(siteId: number, kitValue: number | null, notes: string | null): Promise<void> {
  const { error } = await supabase.from("site_kit_lists").update({ kit_value: kitValue, notes }).eq("site_id", siteId);
  if (error) throw error;
}

export async function deleteKitList(siteId: number): Promise<void> {
  const { error } = await supabase.from("site_kit_lists").delete().eq("site_id", siteId);
  if (error) throw error;
}

export async function linkContractToSite(contractId: number, siteId: number): Promise<void> {
  const { error } = await supabase.from("contract_sites").insert({ contract_id: contractId, site_id: siteId });
  if (error) throw error;
}

export async function unlinkContractFromSite(contractId: number, siteId: number): Promise<void> {
  const { error } = await supabase.from("contract_sites").delete().eq("contract_id", contractId).eq("site_id", siteId);
  if (error) throw error;
}

export interface ContractSiteRow {
  id: number;
  name: string;
  address: string | null;
  city: string | null;
  postcode: string | null;
}

/** Every site currently linked to a contract, for the Locations section
 * of the Contract detail page — a contract can cover one or many sites
 * (contract_sites), so this is never assumed to be a single site. */
export async function fetchContractSites(contractId: number): Promise<ContractSiteRow[]> {
  const { data: links, error: linksErr } = await supabase.from("contract_sites").select("site_id").eq("contract_id", contractId);
  if (linksErr) throw linksErr;
  const siteIds = (links ?? []).map((r) => r.site_id as number);
  if (siteIds.length === 0) return [];
  const { data, error } = await supabase.from("sites").select("id, name, address, city, postcode").in("id", siteIds).order("name");
  if (error) throw error;
  return (data ?? []) as ContractSiteRow[];
}

export async function updateContractRenewalContact(contractId: number, contactId: number | null): Promise<void> {
  const { error } = await supabase.from("contracts").update({ renewal_contact_id: contactId }).eq("id", contractId);
  if (error) throw error;
}

export async function updateContractExclusions(contractId: number, exclusions: string | null): Promise<void> {
  const { error } = await supabase.from("contracts").update({ sla_exclusions: exclusions ?? "" }).eq("id", contractId);
  if (error) throw error;
}

export interface ContractCoreInput {
  company_id: number;
  reference: string | null;
  cover_level: "gold" | "silver" | "bronze";
  start_date: string | null;
  end_date: string | null;
  length_years: number | null;
  not_renewing: boolean;
  not_renewing_reason: string | null;
  notes: string | null;
}

/** value/job_number are deliberately excluded here -- both are masked to
 * null in every read path a caller without can_view_sla_costs uses
 * (v_contracts_display, v_live_contracts), so a form built from that data
 * can't safely let them edit a value they can't see without silently
 * overwriting it. Pass them only from a form gated on can_view_sla_costs. */
export async function createContract(input: ContractCoreInput): Promise<number> {
  const { data, error } = await supabase.from("contracts").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function updateContractCore(contractId: number, input: ContractCoreInput): Promise<void> {
  const { error } = await supabase.from("contracts").update(input).eq("id", contractId);
  if (error) throw error;
}

/** Routed through app.update_contract_value rather than a direct table
 * update -- contracts.value/job_number are write-protected at the column
 * level (0177) precisely so can_edit_contracts alone can't blindly
 * overwrite a value the caller may not even be able to see; the RPC checks
 * can_view_sla_costs too. */
export async function updateContractValue(contractId: number, value: number, jobNumber: string | null): Promise<void> {
  const { error } = await supabase.rpc("update_contract_value", { p_contract_id: contractId, p_value: value, p_job_number: jobNumber });
  if (error) throw error;
}

/* -------------------------------------------------------------- contacts */

export interface ContactSiteRole {
  contact_id: number;
  site_id: number;
  is_service_contact: boolean;
  is_sales_contact: boolean;
}

/**
 * Every contact for a client (company), each carrying its site-scoped
 * roles (if any) for the given site. Reused by the same Contacts panel on
 * both the Contract page (siteId omitted — client-wide view) and the Site
 * page (siteId set) — the Glide app shows an identical "No contacts on
 * record" panel in both places against the same contacts table, per the
 * screenshot pack, so this is one shared read, not two.
 */
export async function fetchContactsForCompany(
  companyId: number,
  siteId?: number,
): Promise<(Contact & { siteRole: ContactSiteRole | null })[]> {
  const [contactsRes, rolesRes] = await Promise.all([
    supabase.from("contacts").select("*").eq("company_id", companyId).is("archived_at", null).order("name"),
    siteId != null
      ? supabase.from("contact_site_roles").select("*").eq("site_id", siteId)
      : Promise.resolve({ data: [] as ContactSiteRole[], error: null }),
  ]);
  if (contactsRes.error) throw contactsRes.error;
  if (rolesRes.error) throw rolesRes.error;
  const roles = new Map((rolesRes.data ?? []).map((r) => [(r as ContactSiteRole).contact_id, r as ContactSiteRole]));
  return ((contactsRes.data ?? []) as Contact[]).map((c) => ({ ...c, siteRole: roles.get(c.id) ?? null }));
}

/** Sets (or clears, if both flags are false) a contact's role at one
 * specific site — the "site can override or supplement the overall
 * client service contact" behaviour. Never touches the contact's
 * client-wide is_default_service_contact/is_default_sales_contact flags. */
export async function setContactSiteRole(
  contactId: number,
  siteId: number,
  roles: { is_service_contact: boolean; is_sales_contact: boolean },
): Promise<void> {
  if (!roles.is_service_contact && !roles.is_sales_contact) {
    const { error } = await supabase.from("contact_site_roles").delete().eq("contact_id", contactId).eq("site_id", siteId);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from("contact_site_roles")
    .upsert({ contact_id: contactId, site_id: siteId, ...roles }, { onConflict: "contact_id,site_id" });
  if (error) throw error;
}

/* ------------------------------------------------------- contract files */

export interface ContractDocument {
  id: number;
  contract_id: number;
  file_id: number;
  doc_type: string | null;
  description: string | null;
  created_at: string;
  storage_path: string;
  size_bytes: number | null;
}

interface ContractDocumentJoinRow {
  id: number;
  contract_id: number;
  file_id: number;
  doc_type: string | null;
  description: string | null;
  created_at: string;
  files: { storage_path: string; size_bytes: number | null } | null;
}

export async function fetchContractDocuments(contractId: number): Promise<ContractDocument[]> {
  const { data, error } = await supabase
    .from("contract_files")
    .select("*, files(storage_path, size_bytes)")
    .eq("contract_id", contractId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data ?? []) as unknown as ContractDocumentJoinRow[]).map((r) => ({
    id: r.id,
    contract_id: r.contract_id,
    file_id: r.file_id,
    doc_type: r.doc_type,
    description: r.description,
    created_at: r.created_at,
    storage_path: r.files?.storage_path ?? "",
    size_bytes: r.files?.size_bytes ?? null,
  }));
}

/** Matches the 10 MB limit the Glide upload card itself states. */
export const CONTRACT_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export async function uploadContractDocument(contractId: number, file: File, uploadedBy: string, note: string | null): Promise<void> {
  if (file.size > CONTRACT_DOCUMENT_MAX_BYTES) throw new Error("File is larger than the 10 MB limit.");
  // Sanitized the same way every other upload path in this codebase does
  // (insurance/RAMS/commissioning) -- a raw file.name here would let an
  // embedded "/" write into an arbitrary sibling storage-key prefix within
  // the documents bucket instead of staying under this contract's own path.
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `contracts/${contractId}/${Date.now()}-${Math.round(Math.random() * 1e6)}-${safeName}`;
  const { error: upErr } = await supabase.storage
    .from("documents")
    .upload(path, file, { contentType: file.type || "application/octet-stream" });
  if (upErr) throw upErr;

  const { data: fileRow, error: fileErr } = await supabase
    .from("files")
    .insert({ bucket_id: "documents", storage_path: path, mime_type: file.type || "application/octet-stream", size_bytes: file.size, uploaded_by: uploadedBy })
    .select("id")
    .single();
  if (fileErr) throw fileErr;

  const { error: linkErr } = await supabase
    .from("contract_files")
    .insert({ contract_id: contractId, file_id: fileRow.id, doc_type: "contract", description: note ?? "" });
  if (linkErr) throw linkErr;
}

export async function deleteContractDocument(id: number): Promise<void> {
  const { error } = await supabase.from("contract_files").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------------ renewal rates */

/** Live config (0165) — was hardcoded (see the comment on the constant
 * above); this reads the business-editable rates, falling back to the
 * prior hardcoded values only if the table is somehow empty. */
export async function fetchRenewalRateTiers(): Promise<Record<"bronze" | "silver" | "gold", number>> {
  const { data, error } = await supabase.from("renewal_rate_tiers").select("cover_level, rate");
  if (error) throw error;
  const byLevel: Record<"bronze" | "silver" | "gold", number> = { ...RENEWAL_RATES };
  for (const r of (data ?? []) as { cover_level: "bronze" | "silver" | "gold"; rate: number }[]) {
    byLevel[r.cover_level] = r.rate;
  }
  return byLevel;
}

export async function updateRenewalRateTier(coverLevel: "bronze" | "silver" | "gold", rate: number, updatedBy: string): Promise<void> {
  const { error } = await supabase
    .from("renewal_rate_tiers")
    .update({ rate, updated_at: new Date().toISOString(), updated_by: updatedBy })
    .eq("cover_level", coverLevel);
  if (error) throw error;
}

/* -------------------------------------------------------- move office */

export interface MoveOfficeInput {
  address: string;
  city: string;
  postcode: string;
  reason: string | null;
}

/**
 * "Move Office" — the client has physically relocated premises (not a
 * re-parenting to a different company). Updates the site's own address in
 * place, keeping the same site id so every room, equipment record, visit,
 * ticket, contract link and contact stays attached across the move, and
 * logs the address being moved FROM for audit. Lat/lng are cleared
 * (no geocoding pipeline exists for sites yet) rather than left stale
 * against the new address.
 */
export async function moveOffice(siteId: number, input: MoveOfficeInput, changedBy: string): Promise<void> {
  const { data: site, error: siteErr } = await supabase
    .from("sites")
    .select("address, city, postcode, lat, lng")
    .eq("id", siteId)
    .maybeSingle();
  if (siteErr) throw siteErr;
  if (!site) throw new Error("Site not found");

  const { error: histErr } = await supabase.from("site_address_history").insert({
    site_id: siteId,
    previous_address: site.address,
    previous_city: site.city,
    previous_postcode: site.postcode,
    previous_lat: site.lat,
    previous_lng: site.lng,
    reason: input.reason,
    changed_by: changedBy,
  });
  if (histErr) throw histErr;

  const { error: updErr } = await supabase
    .from("sites")
    .update({ address: input.address, city: input.city, postcode: input.postcode, lat: null, lng: null, geocoded_at: null })
    .eq("id", siteId);
  if (updErr) throw updErr;
}

export interface SiteAddressHistoryRow {
  id: number;
  previous_address: string | null;
  previous_city: string | null;
  previous_postcode: string | null;
  reason: string | null;
  changed_at: string;
}

export async function fetchSiteAddressHistory(siteId: number): Promise<SiteAddressHistoryRow[]> {
  const { data, error } = await supabase
    .from("site_address_history")
    .select("id, previous_address, previous_city, previous_postcode, reason, changed_at")
    .eq("site_id", siteId)
    .order("changed_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as SiteAddressHistoryRow[];
}

export interface PmFieldsInput {
  pm_last_email_sent: string | null;
  pm_arranged_by: string | null;
  pm_client_confirmed: boolean;
  pm_next_visit_booked_at: string | null;
}

export async function updateSitePmFields(siteId: number, input: PmFieldsInput): Promise<void> {
  const { error } = await supabase.from("sites").update(input).eq("id", siteId);
  if (error) throw error;
}

/* -------------------------------------------------------------- reports */

export interface ServiceReport {
  id: number;
  report_type: "visit" | "client_activity";
  visit_id: number | null;
  company_id: number;
  site_id: number | null;
  period_start: string | null;
  period_end: string | null;
  issued_at: string;
  file_id: number;
  files: { storage_path: string } | null;
  sites: { name: string } | null;
}

export async function fetchVisitReports(visitId: number): Promise<ServiceReport[]> {
  const { data, error } = await supabase
    .from("service_reports")
    .select("*, files(storage_path), sites(name)")
    .eq("visit_id", visitId)
    .order("issued_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as ServiceReport[];
}

export async function fetchClientActivityReports(companyId: number, siteId?: number | null): Promise<ServiceReport[]> {
  let query = supabase
    .from("service_reports")
    .select("*, files(storage_path), sites(name)")
    .eq("company_id", companyId)
    .eq("report_type", "client_activity")
    .order("issued_at", { ascending: false });
  if (siteId != null) query = query.eq("site_id", siteId);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as unknown as ServiceReport[];
}

/** A friendly download filename (e.g. "Client Activity Report - DAC
 * Beachcroft - 2025-08-27 to 2026-08-27.pdf") is set via Storage's
 * signed-URL `download` option rather than renamed in the bucket itself --
 * the storage path stays timestamp-keyed internally (collision-proof,
 * matches every other report type), only the browser-facing name changes. */
export async function signedReportUrl(storagePath: string, downloadFilename?: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from("documents")
    .createSignedUrl(storagePath, 300, downloadFilename ? { download: downloadFilename } : undefined);
  if (error) throw error;
  return data.signedUrl;
}

export async function fetchSitesForCompany(companyId: number): Promise<{ id: number; name: string }[]> {
  const { data, error } = await supabase
    .from("sites")
    .select("id, name")
    .eq("company_id", companyId)
    .is("archived_at", null)
    .order("name");
  if (error) throw error;
  return data ?? [];
}

interface ReportSyncBody {
  ok?: boolean;
  error?: string;
  report_id?: number;
  storage_path?: string;
}

const REPORT_POLL_MS = 1500;
const REPORT_POLL_LIMIT = 40; // ≤ 60s — one visit's data + a single-page PDF render.

/** Renders + persists a report via generate-service-report, relayed the
 * same way as the DVSA/RAM sync buttons (managed devices in this fleet
 * block direct browser POSTs to /functions/v1 — see api.ts's own note
 * on refreshTracking). */
async function requestReport(body: Record<string, unknown>): Promise<ServiceReport> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: "generate-service-report",
    p_body: body,
  });
  if (error) throw new Error(error.message);

  let outcome: { done: boolean; status?: number; body?: ReportSyncBody } = { done: false };
  for (let i = 0; i < REPORT_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, REPORT_POLL_MS));
    const { data, error: pollError } = await supabase.rpc("manual_sync_result", { p_request_id: requestId });
    if (pollError) throw new Error(pollError.message);
    outcome = (data ?? { done: false }) as typeof outcome;
  }
  if (!outcome.done) throw new Error("Report generation is taking longer than expected — check back shortly.");

  const respBody = outcome.body ?? {};
  if (respBody.error) throw new Error(respBody.error);
  if (outcome.status !== undefined && outcome.status >= 400) throw new Error(`Report generation failed (${outcome.status}).`);

  const { data: report, error: reportErr } = await supabase
    .from("service_reports")
    .select("*, files(storage_path), sites(name)")
    .eq("id", respBody.report_id!)
    .single();
  if (reportErr) throw reportErr;
  return report as unknown as ServiceReport;
}

export function generateVisitReport(visitId: number): Promise<ServiceReport> {
  return requestReport({ visit_id: visitId });
}

export function generateClientActivityReport(
  companyId: number,
  periodStart: string,
  periodEnd: string,
  siteId?: number | null,
): Promise<ServiceReport> {
  return requestReport({ company_id: companyId, period_start: periodStart, period_end: periodEnd, site_id: siteId ?? null });
}

/* -------------------------------------------------------------- issues */

/** One row per unrepaired fault, cross-visit -- mirrors v_onsite_issues
 * (0010), which already exists and already backs the Dashboard's "Open
 * faults" count, but nothing before this ever listed the rows themselves. */
export interface OnsiteIssueRow {
  id: number; // service_visit_rooms.id -- the row a Resolve action updates
  visit_id: number;
  visit_reference: string;
  visit_date: string | null;
  company_id: number;
  company_name: string;
  site_id: number | null;
  site_name: string | null;
  room_id: number | null;
  room_name: string | null;
  fault_description: string | null;
  date_fault_raised: string | null;
  actions_to_undertake: string | null;
  date_for_completion: string | null;
  engineer_id: string | null;
  engineer_name: string | null;
  days_open: number | null;
}

export async function fetchOnsiteIssues(): Promise<OnsiteIssueRow[]> {
  const { data, error } = await supabase
    .from("v_onsite_issues")
    .select("*")
    .order("date_fault_raised", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data ?? []) as OnsiteIssueRow[];
}

/** Same direct-update pattern as saveVisitRooms's own field-level writes --
 * service_visit_rooms is RLS-gated on can_edit_visits (0012), not RPC-only,
 * so a one-field "mark resolved" doesn't need its own database function. */
export async function resolveOnsiteIssue(roomRowId: number): Promise<void> {
  const { error } = await supabase
    .from("service_visit_rooms")
    .update({ fault_repaired: true, date_of_repair: new Date().toISOString().slice(0, 10) })
    .eq("id", roomRowId);
  if (error) throw error;
}

/* ------------------------------------------------------- Recurring Services */
// recurring_services / recurring_service_suppliers have existed since the
// original schema foundation (0005) -- RLS-gated on can_view_service (read)
// / can_edit_recurring_services (write), 0012/0018 -- but never had any UI.
// No cost masking exists on annual_cost (unlike contracts.value): it's a
// plain column, visible to anyone with service access.

export interface RecurringServiceSupplierRow {
  id: number;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
}

export interface RecurringServiceSupplierInput {
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
}

export async function fetchRecurringServiceSuppliers(): Promise<RecurringServiceSupplierRow[]> {
  const { data, error } = await supabase.from("recurring_service_suppliers").select("*").order("name");
  if (error) throw error;
  return (data ?? []) as RecurringServiceSupplierRow[];
}

export async function createRecurringServiceSupplier(input: RecurringServiceSupplierInput): Promise<number> {
  const { data, error } = await supabase.from("recurring_service_suppliers").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function updateRecurringServiceSupplier(id: number, input: RecurringServiceSupplierInput): Promise<void> {
  const { error } = await supabase.from("recurring_service_suppliers").update(input).eq("id", id);
  if (error) throw error;
}

export interface RecurringServiceRow {
  id: number;
  company_id: number;
  company_name: string;
  site_id: number | null;
  site_name: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  service_name: string;
  frequency: string | null;
  annual_cost: number | null;
  next_due_date: string | null;
  last_done_date: string | null;
  notes: string | null;
  is_active: boolean;
}

export interface RecurringServiceInput {
  company_id: number;
  site_id: number | null;
  supplier_id: number | null;
  service_name: string;
  frequency: string | null;
  annual_cost: number | null;
  next_due_date: string | null;
  last_done_date: string | null;
  notes: string | null;
}

const RECURRING_SERVICE_SELECT =
  "id, company_id, site_id, next_due_date, last_done_date, service_name, frequency, annual_cost, notes, is_active, supplier_id, companies(name), sites(name), recurring_service_suppliers(name)";

interface RecurringServiceJoinRow {
  id: number;
  company_id: number;
  site_id: number | null;
  next_due_date: string | null;
  last_done_date: string | null;
  service_name: string;
  frequency: string | null;
  annual_cost: number | null;
  notes: string | null;
  is_active: boolean;
  supplier_id: number | null;
  companies: { name: string } | null;
  sites: { name: string } | null;
  recurring_service_suppliers: { name: string } | null;
}

function mapRecurringService(r: RecurringServiceJoinRow): RecurringServiceRow {
  return {
    id: r.id,
    company_id: r.company_id,
    company_name: r.companies?.name ?? "",
    site_id: r.site_id,
    site_name: r.sites?.name ?? null,
    supplier_id: r.supplier_id,
    supplier_name: r.recurring_service_suppliers?.name ?? null,
    service_name: r.service_name,
    frequency: r.frequency,
    annual_cost: r.annual_cost,
    next_due_date: r.next_due_date,
    last_done_date: r.last_done_date,
    notes: r.notes,
    is_active: r.is_active,
  };
}

/** All active services, across every client -- the "Manage all" page. */
export async function fetchRecurringServices(): Promise<RecurringServiceRow[]> {
  const { data, error } = await supabase
    .from("recurring_services")
    .select(RECURRING_SERVICE_SELECT)
    .eq("is_active", true)
    .order("next_due_date", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return ((data ?? []) as unknown as RecurringServiceJoinRow[]).map(mapRecurringService);
}

/** Scoped to one client -- the compact card on ContractDetail.tsx. */
export async function fetchRecurringServicesForCompany(companyId: number): Promise<RecurringServiceRow[]> {
  const { data, error } = await supabase
    .from("recurring_services")
    .select(RECURRING_SERVICE_SELECT)
    .eq("company_id", companyId)
    .eq("is_active", true)
    .order("next_due_date", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return ((data ?? []) as unknown as RecurringServiceJoinRow[]).map(mapRecurringService);
}

export async function createRecurringService(input: RecurringServiceInput): Promise<number> {
  const { data, error } = await supabase.from("recurring_services").insert(input).select("id").single();
  if (error) throw error;
  return data.id;
}

export async function updateRecurringService(id: number, input: RecurringServiceInput): Promise<void> {
  const { error } = await supabase.from("recurring_services").update(input).eq("id", id);
  if (error) throw error;
}

/** Soft-delete via is_active=false, matching the column this table has
 * always had for exactly this -- no archived_at here, unlike most of the
 * rest of the schema. */
export async function archiveRecurringService(id: number): Promise<void> {
  const { error } = await supabase.from("recurring_services").update({ is_active: false }).eq("id", id);
  if (error) throw error;
}

/** Bumps next_due_date forward by one billing cycle from itself (or from
 * today if it was never set), and records today as last_done_date -- the
 * one-click "renew" action, done client-side since it's simple date math
 * with no pricing implication. */
export function nextDueDateAfter(current: string | null, frequency: string | null): string {
  const base = current ? new Date(`${current}T00:00:00`) : new Date();
  const months = frequency === "monthly" ? 1 : frequency === "quarterly" ? 3 : 12;
  const next = new Date(base);
  next.setMonth(next.getMonth() + months);
  return next.toISOString().slice(0, 10);
}

export async function renewRecurringService(id: number, nextDueDate: string): Promise<void> {
  const { error } = await supabase
    .from("recurring_services")
    .update({ next_due_date: nextDueDate, last_done_date: new Date().toISOString().slice(0, 10) })
    .eq("id", id);
  if (error) throw error;
}

/* ============================================================ Recurring
 * service CONTRACTS (full model, 0581/0582). A recurring service is a
 * dated contract with a supplier, category, client + locations, buy/sell
 * price, job number, documents, and a preserved renewal history. Status is
 * derived from the dates (Live/Expired) plus a Not-Renewing flag. Kept
 * separate from the legacy read functions above so the compact card on
 * ContractDetail keeps working unchanged. */

export type RecurringServiceStatus = "live" | "expired" | "not_renewing";
export type RecurringServiceCategory = "license" | "support" | "license_support";

export const RECURRING_SERVICE_CATEGORIES: { value: RecurringServiceCategory; label: string }[] = [
  { value: "license", label: "License" },
  { value: "support", label: "Support" },
  { value: "license_support", label: "License & Support" },
];

export function recurringCategoryLabel(v: string | null): string {
  return RECURRING_SERVICE_CATEGORIES.find((c) => c.value === v)?.label ?? "—";
}

export function recurringStatusLabel(s: string | null): string {
  return s === "expired" ? "Expired" : s === "not_renewing" ? "Not Renewing" : s === "live" ? "Live" : "—";
}

/** "12 months" / "24 months" / "3 years" from a whole-month count. */
export function contractLengthLabel(months: number | null): string {
  if (months == null) return "—";
  if (months > 0 && months % 12 === 0) {
    const y = months / 12;
    return `${y} year${y === 1 ? "" : "s"}`;
  }
  return `${months} month${months === 1 ? "" : "s"}`;
}

export interface RecurringServiceContractRow {
  id: number;
  company_id: number;
  company_name: string;
  supplier_id: number | null;
  supplier_name: string | null;
  service_category: RecurringServiceCategory | null;
  associated_equipment_id: number | null;
  service_name: string;
  service_description: string | null;
  buy_price: number | null;
  sell_price: number | null;
  /** Licence/unit count (default 1). */
  quantity: number;
  /** Derived in v_recurring_services: sell_price * quantity (null when no sell price). */
  sell_total: number | null;
  contract_start_date: string | null;
  contract_end_date: string | null;
  job_number: string | null;
  location_scope: "all" | "selected";
  not_renewing: boolean;
  not_renewing_reason: string | null;
  not_renewing_at: string | null;
  notes: string | null;
  status: RecurringServiceStatus;
  is_current: boolean;
  contract_length_months: number | null;
  location_names: string | null;
  document_count: number;
  renewal_count: number;
}

export async function fetchRecurringServiceContracts(): Promise<RecurringServiceContractRow[]> {
  const { data, error } = await supabase
    .from("v_recurring_services")
    .select("*")
    .order("contract_end_date", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data ?? []) as RecurringServiceContractRow[];
}

export async function fetchRecurringServiceContract(id: number): Promise<RecurringServiceContractRow | null> {
  const { data, error } = await supabase.from("v_recurring_services").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return (data ?? null) as RecurringServiceContractRow | null;
}

/** The recurring-service contracts for one client, current ones first then by
 * soonest end date — for the summary card on the customer Overview page.
 * RLS on v_recurring_services gates the rows on can_view_service, so a caller
 * without it simply gets an empty list. */
export async function fetchRecurringServiceContractsForCompany(
  companyId: number,
): Promise<RecurringServiceContractRow[]> {
  const { data, error } = await supabase
    .from("v_recurring_services")
    .select("*")
    .eq("company_id", companyId)
    .order("is_current", { ascending: false })
    .order("contract_end_date", { ascending: true, nullsFirst: false });
  if (error) throw error;
  return (data ?? []) as RecurringServiceContractRow[];
}

/* ------------------------------------------------- Renewal document (print) */

/**
 * Data for the branded, printable renewal document (RenewalDocument.tsx) — a
 * 1:1 port of Glide's RenewalQuoteView layout. Field names mirror the shape
 * that Glide's /app-api/renewal-quotes/:id returned, so the ported component
 * reads them unchanged; this function just assembles that shape from Core's
 * own tables (renewal_quotes + contract + company + sites + recurring
 * services). cover_level is Title-cased to match the document's Gold/Silver/
 * Bronze labels and COVER_COLORS keys.
 */
export interface RenewalDocumentData {
  client: string;
  client_website: string | null;
  renewal_start_date: string | null;
  renewal_end_date: string | null;
  cover_level: string;
  total_equipment_value: number;
  bronze_guide_price: number;
  silver_guide_price: number;
  gold_guide_price: number;
  quoted_price: number;
  pricing_visibility: Record<string, boolean>;
  site_breakdown: { location_name: string; address: string | null; kit_list_value: number }[];
  recurring_services: {
    service_name: string;
    description: string | null;
    supplier_name: string | null;
    billing_cycle: string;
    cost_to_client: number | null;
  }[];
  sla_exclusions: string | null;
}

export async function fetchRenewalDocument(id: number): Promise<RenewalDocumentData> {
  // Read through the SECURITY DEFINER display view, NOT the base table. The
  // `authenticated` role has no SELECT grant on renewal_quotes (reads go via
  // this view, writes via the create_renewal_quote SECURITY DEFINER fn), so
  // hitting the base table threw "permission denied for table renewal_quotes"
  // and the viewer hung on its spinner forever (2026-11-05). The view exposes
  // site_breakdown + pricing_visibility and gates quoted_value/site_breakdown
  // on can_view_sla_costs, which the quote's creator necessarily has.
  const { data: rq, error } = await supabase
    .from("v_renewal_quotes_display")
    .select("*")
    .eq("id", id)
    .single();
  if (error) throw error;

  const sb = (rq.site_breakdown ?? {}) as {
    kit_value?: number;
    selected_cover_level?: string;
    tiers?: { coverLevel: string; flatRatePrice: number; proRataPrice: number }[];
    site_values?: { site_id: number; site_name: string; value: number }[];
  };

  const { data: contract, error: cErr } = await supabase
    .from("contracts")
    .select("company_id, reference, cover_level, sla_exclusions")
    .eq("id", rq.contract_id)
    .single();
  if (cErr) throw cErr;

  const { data: company } = await supabase
    .from("companies")
    .select("name, website")
    .eq("id", contract.company_id)
    .maybeSingle();

  const siteValues = sb.site_values ?? [];
  const addressBySite = new Map<number, string | null>();
  if (siteValues.length > 0) {
    const { data: sites } = await supabase
      .from("sites")
      .select("id, address, city, postcode")
      .in("id", siteValues.map((s) => s.site_id));
    for (const s of sites ?? []) {
      const parts = [s.address, s.city, s.postcode].filter(Boolean);
      addressBySite.set(s.id as number, parts.length ? parts.join(", ") : null);
    }
  }

  const tierPrice = (level: string) => sb.tiers?.find((t) => t.coverLevel === level)?.flatRatePrice ?? 0;
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  // Core doesn't snapshot recurring services onto the quote (the column is
  // present but empty), so embed the client's live, currently-renewing ones,
  // the way Glide's document did. Core's recurring model is annual (sell_total
  // = sell_price x quantity), so each is reported as an Annual line.
  const recurring = (await fetchRecurringServiceContractsForCompany(contract.company_id))
    .filter((r) => r.is_current && !r.not_renewing)
    .map((r) => ({
      service_name: r.service_name,
      description: r.service_description,
      supplier_name: r.supplier_name,
      billing_cycle: "Annual",
      cost_to_client: r.sell_total,
    }));

  return {
    client: company?.name ?? "Unknown company",
    client_website: company?.website ?? null,
    renewal_start_date: rq.new_start_date,
    renewal_end_date: rq.new_end_date,
    cover_level: cap(sb.selected_cover_level ?? contract.cover_level ?? ""),
    total_equipment_value: sb.kit_value ?? 0,
    bronze_guide_price: tierPrice("bronze"),
    silver_guide_price: tierPrice("silver"),
    gold_guide_price: tierPrice("gold"),
    quoted_price: rq.quoted_value ?? 0,
    pricing_visibility: (rq.pricing_visibility ?? {}) as Record<string, boolean>,
    site_breakdown: siteValues.map((s) => ({
      location_name: s.site_name,
      address: addressBySite.get(s.site_id) ?? null,
      kit_list_value: s.value,
    })),
    recurring_services: recurring,
    sla_exclusions: contract.sla_exclusions || null,
  };
}

export interface RecurringServiceContractInput {
  company_id: number;
  supplier_id: number | null;
  service_category: RecurringServiceCategory | null;
  associated_equipment_id: number | null;
  service_name: string;
  service_description: string | null;
  buy_price: number | null;
  sell_price: number | null;
  quantity: number;
  contract_start_date: string | null;
  contract_end_date: string | null;
  job_number: string | null;
  location_scope: "all" | "selected";
  notes: string | null;
}

/** Create or update, then replace the location set. Locations only apply
 * when scope = 'selected'; 'all' means every client location. */
export async function saveRecurringServiceContract(
  id: number | null,
  input: RecurringServiceContractInput,
  siteIds: number[],
): Promise<number> {
  let serviceId = id;
  if (serviceId == null) {
    const { data, error } = await supabase.from("recurring_services").insert(input).select("id").single();
    if (error) throw error;
    serviceId = data.id as number;
  } else {
    const { error } = await supabase.from("recurring_services").update({ ...input, updated_at: new Date().toISOString() }).eq("id", serviceId);
    if (error) throw error;
  }
  const { error: delErr } = await supabase.from("recurring_service_locations").delete().eq("recurring_service_id", serviceId);
  if (delErr) throw delErr;
  if (input.location_scope === "selected" && siteIds.length > 0) {
    const { error: insErr } = await supabase
      .from("recurring_service_locations")
      .insert(siteIds.map((s) => ({ recurring_service_id: serviceId, site_id: s })));
    if (insErr) throw insErr;
  }
  return serviceId;
}

export async function markRecurringServiceNotRenewing(id: number, reason: string): Promise<void> {
  const { error } = await supabase
    .from("recurring_services")
    .update({ not_renewing: true, not_renewing_reason: reason, not_renewing_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export interface RecurringRenewalInput {
  contract_start_date: string;
  contract_end_date: string;
  job_number: string | null;
  buy_price: number | null;
  sell_price: number | null;
}

/** Atomic renew: archives the current period + its documents, rolls the
 * live record onto the new period. Old data is never overwritten (0582). */
export async function renewRecurringServiceContract(id: number, input: RecurringRenewalInput): Promise<void> {
  const { error } = await supabase.rpc("renew_recurring_service", { p_id: id, p: input });
  if (error) throw error;
}

export interface RecurringServiceRenewalRow {
  id: number;
  buy_price: number | null;
  sell_price: number | null;
  contract_start_date: string | null;
  contract_end_date: string | null;
  job_number: string | null;
  not_renewing: boolean | null;
  not_renewing_reason: string | null;
  archived_at: string;
}

export async function fetchRecurringServiceRenewals(id: number): Promise<RecurringServiceRenewalRow[]> {
  const { data, error } = await supabase
    .from("recurring_service_renewals")
    .select("id, buy_price, sell_price, contract_start_date, contract_end_date, job_number, not_renewing, not_renewing_reason, archived_at")
    .eq("recurring_service_id", id)
    .order("archived_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as RecurringServiceRenewalRow[];
}

export async function fetchRecurringServiceLocations(id: number): Promise<{ id: number; name: string }[]> {
  const { data, error } = await supabase
    .from("recurring_service_locations")
    .select("sites(id, name)")
    .eq("recurring_service_id", id);
  if (error) throw error;
  return ((data ?? []) as unknown as { sites: { id: number; name: string } | null }[])
    .map((r) => r.sites)
    .filter((s): s is { id: number; name: string } => s != null)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface RecurringServiceDocument {
  id: number;
  file_id: number;
  renewal_id: number | null;
  description: string | null;
  created_at: string;
  storage_path: string;
  size_bytes: number | null;
}

export async function fetchRecurringServiceDocuments(id: number): Promise<RecurringServiceDocument[]> {
  const { data, error } = await supabase
    .from("recurring_service_files")
    .select("id, file_id, renewal_id, description, created_at, files(storage_path, size_bytes)")
    .eq("recurring_service_id", id)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data ?? []) as unknown as {
    id: number; file_id: number; renewal_id: number | null; description: string | null; created_at: string;
    files: { storage_path: string; size_bytes: number | null } | null;
  }[]).map((r) => ({
    id: r.id, file_id: r.file_id, renewal_id: r.renewal_id, description: r.description, created_at: r.created_at,
    storage_path: r.files?.storage_path ?? "", size_bytes: r.files?.size_bytes ?? null,
  }));
}

export async function uploadRecurringServiceDocument(serviceId: number, file: File, uploadedBy: string, note: string | null): Promise<void> {
  if (file.size > CONTRACT_DOCUMENT_MAX_BYTES) throw new Error("File is larger than the 10 MB limit.");
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `recurring-services/${serviceId}/${Date.now()}-${Math.round(Math.random() * 1e6)}-${safeName}`;
  const { error: upErr } = await supabase.storage.from("documents").upload(path, file, { contentType: file.type || "application/octet-stream" });
  if (upErr) throw upErr;
  const { data: fileRow, error: fileErr } = await supabase
    .from("files")
    .insert({ bucket_id: "documents", storage_path: path, mime_type: file.type || "application/octet-stream", size_bytes: file.size, uploaded_by: uploadedBy })
    .select("id")
    .single();
  if (fileErr) throw fileErr;
  const { error: linkErr } = await supabase
    .from("recurring_service_files")
    .insert({ recurring_service_id: serviceId, file_id: fileRow.id, description: note ?? "" });
  if (linkErr) throw linkErr;
}

export async function deleteRecurringServiceDocument(id: number): Promise<void> {
  const { error } = await supabase.from("recurring_service_files").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------- PM management board */

export type PmRag = "red" | "amber" | "green" | "grey";
export type PmBookingStage = "not_started" | "chased" | "booked" | "confirmed";
export type PmVisitStatus = "never_visited" | "overdue" | "due_soon" | "current" | "no_contract";

/** One site's PM state for the management board (v_pm_board, 0595): Core's
 * own 6-month cadence + live-contract scope, plus the booking workflow and a
 * RAG code. `on_live_contract` is false for lapsed/unlinked sites (rag grey),
 * which carry no PM obligation and are hidden by default. */
export interface PmBoardRow {
  site_id: number;
  site_name: string;
  company_id: number;
  company_name: string;
  company_website: string | null;
  city: string | null;
  last_pm_visit: string | null;
  pm_visit_status: PmVisitStatus;
  next_pm_due: string | null;
  /** Positive once past due; negative = days until due; null when no due date. */
  days_overdue: number | null;
  pm_next_visit_booked_at: string | null;
  pm_client_confirmed: boolean;
  pm_last_email_sent: string | null;
  pm_arranged_by: string | null;
  contract_id: number | null;
  cover_level: "gold" | "silver" | "bronze" | null;
  contract_end_date: string | null;
  on_live_contract: boolean;
  booking_stage: PmBookingStage;
  rag: PmRag;
  /** Site coordinates for the map/heat view; null when not geocoded. */
  lat: number | null;
  lng: number | null;
}

export async function fetchPmBoard(): Promise<PmBoardRow[]> {
  const { data, error } = await supabase
    .from("v_pm_board")
    .select("*")
    .order("days_overdue", { ascending: false, nullsFirst: false });
  if (error) throw error;
  return (data ?? []) as PmBoardRow[];
}

export interface PmBookingInput {
  site_id: number;
  /** ISO date/datetime for the booked visit, or null to clear it. */
  pm_next_visit_booked_at: string | null;
  pm_client_confirmed: boolean;
  /** ISO date the chase email went to the client, or null. */
  pm_last_email_sent: string | null;
  pm_arranged_by: string | null;
}

/** Full-replace of a site's four PM booking fields via the guarded RPC
 * (0595, gated on can_edit_visits). The board row carries the current values,
 * so one-click actions (log chase / confirm / clear) resubmit the rest. */
export async function setPmBooking(input: PmBookingInput): Promise<void> {
  const { error } = await supabase.rpc("set_pm_booking", {
    p_site_id: input.site_id,
    p_next_visit_booked_at: input.pm_next_visit_booked_at,
    p_client_confirmed: input.pm_client_confirmed,
    p_last_email_sent: input.pm_last_email_sent,
    p_arranged_by: input.pm_arranged_by,
  });
  if (error) throw new Error(error.message);
}

/** Devices (equipment) at the selected client's sites, for the optional
 * "Associated Device" link. */
export async function fetchDevicesForCompany(companyId: number): Promise<{ id: number; label: string }[]> {
  const { data: sites, error: sErr } = await supabase.from("sites").select("id").eq("company_id", companyId);
  if (sErr) throw sErr;
  const siteIds = (sites ?? []).map((s) => s.id as number);
  if (siteIds.length === 0) return [];
  const { data, error } = await supabase
    .from("equipment")
    .select("id, category, model_number, serial_number, manufacturers(name), equipment_models(model_number), sites(name)")
    .in("site_id", siteIds)
    .is("archived_at", null)
    .order("id");
  if (error) throw error;
  return ((data ?? []) as unknown as {
    id: number; category: string | null; model_number: string | null; serial_number: string | null;
    manufacturers: { name: string } | null; equipment_models: { model_number: string } | null; sites: { name: string } | null;
  }[]).map((e) => {
    const make = e.manufacturers?.name ?? "";
    const model = e.equipment_models?.model_number ?? e.model_number ?? "";
    const sn = e.serial_number ? ` (SN ${e.serial_number})` : "";
    const site = e.sites?.name ? ` — ${e.sites.name}` : "";
    const label = `${[make, model].filter(Boolean).join(" ") || e.category || "Device"}${sn}${site}`;
    return { id: e.id, label };
  });
}
